/**
 * FM Job Detail Page
 * Full job detail with tabs: Summary, Quotes, Works/Updates, Attachments, Financials, History
 * Implements Phase 2 Workflows: Completion Pack, Review Gate, Invoice Control
 * Uses unified JobCard for main job display
 * Includes Next Action Engine for operational guidance
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import UpliftPanel from '@/components/UpliftPanel';
import JobCard from '@/components/JobCard';
import { JobRecommendationPanel, ContractorCapacityCard } from '@/components/MarketplaceDashboard';
import JobActionButtons from '@/components/JobActionButtons';
import { 
  ArrowLeft, Building2, MapPin, Calendar, User, Clock, AlertTriangle, CheckCircle,
  FileText, DollarSign, Upload, Paperclip, History, Wrench, MessageSquare,
  UserPlus, AlertOctagon, ExternalLink, Plus, Trash2, Edit, Save, X,
  Camera, FileCheck, ClipboardCheck, Receipt, XCircle, HelpCircle, Image,
  Share2, Download, Mail, Phone, Send, RefreshCw, PhoneCall, MessageCircle,
  Zap
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// =====================================================
// NEXT ACTION BANNER COMPONENT
// =====================================================

const NextActionBanner = ({ nextAction, status, reassignCount }) => {
  if (!nextAction) return null;
  
  const getStyle = () => {
    if (nextAction.includes("ESCALATED") || nextAction.includes("Manual")) {
      return "bg-red-600 border-red-700";
    }
    if (nextAction.includes("Review") || nextAction.includes("Assign") || nextAction.includes("Schedule")) {
      return "bg-orange-500 border-orange-600";
    }
    if (nextAction.includes("Waiting") || nextAction.includes("Awaiting")) {
      return "bg-blue-500 border-blue-600";
    }
    if (nextAction.includes("No action") || nextAction.includes("closed")) {
      return "bg-slate-400 border-slate-500";
    }
    return "bg-[#1F7A78] border-[#16615f]";
  };

  return (
    <div className={`p-4 text-white font-medium rounded-lg mb-4 border-l-4 shadow-sm flex items-center justify-between ${getStyle()}`} data-testid="next-action-banner">
      <div className="flex items-center gap-3">
        <Zap className="h-5 w-5" />
        <div>
          <span className="text-white/80 text-xs uppercase tracking-wide">Next Action</span>
          <p className="text-white font-semibold">{nextAction}</p>
        </div>
      </div>
      {reassignCount > 0 && (
        <Badge className="bg-white/20 text-white border-white/30">
          Reassign Cycle: {reassignCount}
        </Badge>
      )}
    </div>
  );
};

// Safe array helper - ensures we always have an array
const safeArray = (value) => Array.isArray(value) ? value : [];

// Wyldecrest Stabilised Workflow - 8 Stages Only
const STATUS_COLORS = {
  'Job Created': 'bg-blue-100 text-blue-700',
  'Awaiting Quote': 'bg-purple-100 text-purple-700',
  'Quote Awaiting Approval': 'bg-amber-100 text-amber-700',
  'Approved': 'bg-green-100 text-green-700',
  'Assigned': 'bg-cyan-100 text-cyan-700',
  'Scheduled': 'bg-teal-100 text-teal-700',
  'Completed': 'bg-emerald-100 text-emerald-700',
  'Closed': 'bg-slate-100 text-slate-600',
};

const PRIORITY_COLORS = {
  'Low': 'bg-gray-100 text-gray-600',
  'Medium': 'bg-blue-100 text-blue-700',
  'High': 'bg-orange-100 text-orange-700',
  'Urgent': 'bg-red-100 text-red-700',
  'Emergency': 'bg-red-200 text-red-800',
  'Critical': 'bg-red-200 text-red-800',
};

// =====================================================
// INSPECTIONS TAB COMPONENT
// =====================================================

const InspectionsTab = ({ jobId, jobRef, siteName, plot }) => {
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const navigate = useNavigate();
  
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const loadInspections = useCallback(async () => {
    if (!jobId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/inspection-reports/job/${jobId}`, { headers });
      setInspections(res.data.reports || []);
    } catch (err) {
      console.error('Failed to load inspections:', err);
      setInspections([]);
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    loadInspections();
  }, [loadInspections]);

  const handleCreateInspection = () => {
    // Navigate to inspection report page with job context
    const params = new URLSearchParams({
      job_id: jobId,
      ref: jobRef || '',
      site: siteName || '',
      location: plot || ''
    });
    navigate(`/inspection-reports?${params.toString()}`);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return format(parseISO(dateStr), 'dd MMM yyyy');
    } catch {
      return dateStr;
    }
  };

  return (
    <Card className="border-0 shadow-sm">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <ClipboardCheck className="h-5 w-5 text-[#1F7A78]" />
            Inspection Reports
          </CardTitle>
          <Button size="sm" onClick={handleCreateInspection}>
            <Plus className="h-4 w-4 mr-1" />
            New Inspection
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-8 text-slate-500">
            <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />
            Loading inspections...
          </div>
        ) : inspections.length === 0 ? (
          <div className="text-center py-8">
            <ClipboardCheck className="h-12 w-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500 mb-4">No inspection reports for this job</p>
            <Button variant="outline" onClick={handleCreateInspection}>
              <Plus className="h-4 w-4 mr-1" />
              Create First Inspection
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {inspections.map((inspection) => (
              <div 
                key={inspection.id}
                className="p-4 border rounded-lg hover:bg-slate-50 cursor-pointer transition-colors"
                onClick={() => navigate(`/inspection-reports?report_id=${inspection.id}`)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-[#1F7A78]">
                        {inspection.ref || `INS-${inspection.id?.slice(-6)}`}
                      </span>
                      <Badge className={
                        inspection.status === 'final' ? 'bg-emerald-100 text-emerald-700' :
                        inspection.status === 'pending_review' ? 'bg-amber-100 text-amber-700' :
                        'bg-slate-100 text-slate-600'
                      }>
                        {inspection.status || 'draft'}
                      </Badge>
                    </div>
                    <p className="text-sm text-slate-600">
                      {inspection.site} {inspection.location && `• ${inspection.location}`}
                    </p>
                    {inspection.summary && (
                      <p className="text-sm text-slate-500 mt-1 line-clamp-2">
                        {inspection.summary}
                      </p>
                    )}
                  </div>
                  <div className="text-right text-sm">
                    <p className="text-slate-500">{formatDate(inspection.date || inspection.created_at)}</p>
                    {inspection.inspector && (
                      <p className="text-slate-400 text-xs">{inspection.inspector}</p>
                    )}
                  </div>
                </div>
                {inspection.findings && (
                  <div className="mt-2 pt-2 border-t">
                    <p className="text-xs text-slate-500 font-medium">Key Findings:</p>
                    <p className="text-sm text-slate-600 line-clamp-1">{inspection.findings}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        
        {inspections.length > 0 && (
          <div className="mt-4 pt-4 border-t flex justify-between items-center">
            <span className="text-sm text-slate-500">
              {inspections.length} inspection{inspections.length !== 1 ? 's' : ''} linked to this job
            </span>
            <Button variant="outline" size="sm" onClick={handleCreateInspection}>
              <Plus className="h-4 w-4 mr-1" />
              Add Another
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Map job to unified format for JobCard
function mapJobToUnified(job) {
  if (!job) return null;
  return {
    job_id: job.id || job._id || job.job_id,
    job_reference: job.job_reference,
    park_name: job.park_name || job.site_name,
    unit_name: job.plot_number || job.unit_or_pitch || job.asset_name,
    full_address: job.full_address || job.site_address || job.address || job.address_line_1 || '',
    postcode: job.postcode,
    contact_name: job.contact_name || job.resident_name || job.site_contact_name || job.reported_by,
    contact_phone: job.contact_phone || job.phone || job.contact_number,
    contact_email: job.contact_email || job.email,
    job_type: job.job_type || job.job_type_label || job.task_name,
    trade: job.trade,
    priority: job.priority || 'Standard',
    description: job.description || job.issue || job.title,
    access_notes: typeof job.access_notes === 'string' ? job.access_notes : (typeof job.notes === 'string' ? job.notes : ''),
    created_date: job.created_at || job.created_date,
    scheduled_date: job.scheduled_date,
    due_date: job.due_date || job.sla_due_date || job.target_completion_date,
    stage: job.stage || job.status,
    status: job.status || job.stage,
    assigned_contractor_id: job.assigned_contractor_id,
    assigned_contractor_name: job.assigned_contractor_name,
    quote_value: job.latest_quote_total_inc_vat || job.quote_value || job.quoted_total || job.approved_value,
    po_reference: job.po_reference || job.po_number,
    invoice_id: job.invoice_id || job.invoice_reference,
  };
}

export default function FMJobDetailPage() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('summary');
  const [contractors, setContractors] = useState([]);
  
  // Dialogs
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showNoteDialog, setShowNoteDialog] = useState(false);
  const [showCompletionDialog, setShowCompletionDialog] = useState(false);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [showQuoteDialog, setShowQuoteDialog] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareEmail, setShareEmail] = useState('');
  const [sharePhone, setSharePhone] = useState('');
  const [shareLoading, setShareLoading] = useState(false);
  
  // Form states
  const [assignData, setAssignData] = useState({ contractor_id: '', scheduled_date: '', scheduled_time: '' });
  const [noteText, setNoteText] = useState('');
  const [noteAttachments, setNoteAttachments] = useState([]);  // For note file attachments
  const [noteUploading, setNoteUploading] = useState(false);
  const [completionData, setCompletionData] = useState({
    before_photos: [],
    during_photos: [],
    after_photos: [],
    completion_notes: '',
    works_summary: '',
    materials_used: '',
    compliance_documents: []
  });
  const [reviewData, setReviewData] = useState({
    outcome: 'Approve',
    comments: '',
    scope_aligned: true,
    satisfactory: true,
    evidence_complete: true,
    compliance_complete: true
  });
  const [invoiceData, setInvoiceData] = useState({
    amount: '',
    invoice_number: '',
    invoice_date: '',
    notes: ''
  });
  const [quoteData, setQuoteData] = useState({
    line_items: [{ description: '', quantity: 1, unit_cost: 0 }],
    notes: ''
  });
  
  // Completion pack and reviews
  const [completionPack, setCompletionPack] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [invoice, setInvoice] = useState(null);
  
  // Final Operational Patch states
  const [showCompletionCheckDialog, setShowCompletionCheckDialog] = useState(false);
  const [completionCheck, setCompletionCheck] = useState(null);
  const [completionCheckLoading, setCompletionCheckLoading] = useState(false);
  const [showUploadDocsDialog, setShowUploadDocsDialog] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [showCreateInvoiceDialog, setShowCreateInvoiceDialog] = useState(false);
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [showApproveQuoteDialog, setShowApproveQuoteDialog] = useState(false);
  const [approvingQuote, setApprovingQuote] = useState(false);
  
  // Action Centre States
  const [actionHistory, setActionHistory] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);
  const [showOutcomeSheet, setShowOutcomeSheet] = useState(false);
  const [pendingActionId, setPendingActionId] = useState(null);
  const [pendingActionType, setPendingActionType] = useState(null);
  const [outcomeStatus, setOutcomeStatus] = useState('');
  const [outcomeNote, setOutcomeNote] = useState('');
  const [followUpDate, setFollowUpDate] = useState('');
  const [recommendedAction, setRecommendedAction] = useState(null);
  
  // Next Action Engine States
  const [nextActionData, setNextActionData] = useState(null);
  const [quoteRequests, setQuoteRequests] = useState([]);
  const [selectingQuote, setSelectingQuote] = useState(false);
  
  const token = localStorage.getItem('token');
  const headers = React.useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  
  const loadJob = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/fm/jobs/${jobId}`, { headers });
      setJob(res.data.job);
    } catch (err) {
      console.error('Load job error:', err);
      // Only show toast on first load error, not on refresh failures
      if (!job) {
        toast.error('Failed to load job details');
      }
    } finally {
      setLoading(false);
    }
  }, [jobId, headers, job]);
  
  const loadCompletionPack = async () => {
    try {
      const res = await axios.get(`${API}/api/fm/jobs/${jobId}/completion-pack`, { headers });
      setCompletionPack(res.data.completion_pack);
    } catch (err) {
      console.error('Load completion pack error:', err);
    }
  };
  
  const loadReviews = async () => {
    try {
      const res = await axios.get(`${API}/api/fm/jobs/${jobId}/reviews`, { headers });
      setReviews(res.data.reviews || []);
    } catch (err) {
      console.error('Load reviews error:', err);
    }
  };
  
  const loadInvoice = async () => {
    try {
      const res = await axios.get(`${API}/api/fm/jobs/${jobId}/invoice`, { headers });
      setInvoice(res.data.invoice);
    } catch (err) {
      console.error('Load invoice error:', err);
    }
  };
  
  const loadContractors = async () => {
    try {
      const res = await axios.get(`${API}/api/contractors`, { headers });
      setContractors(res.data || []);
    } catch (err) {
      console.error('Load contractors error:', err);
    }
  };
  
  // Load action history for Action Centre
  const loadActionHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/fm/jobs/${jobId}/actions/history`, { headers });
      setActionHistory(res.data.action_logs || []);
      if (res.data.last_action) {
        // Could set recommended action based on decision engine
      }
    } catch (err) {
      console.error('Load action history error:', err);
    }
  }, [jobId, headers]);
  
  // Load Next Action from Control Engine
  const loadNextAction = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/control/job/${jobId}/next-action`);
      setNextActionData(res.data);
    } catch (err) {
      console.error('Load next action error:', err);
    }
  }, [jobId]);
  
  // Load Quote Requests for this job
  const loadQuoteRequests = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/lhs/jobs/${jobId}/quote-requests`);
      setQuoteRequests(res.data.quote_requests || []);
    } catch (err) {
      console.error('Load quote requests error:', err);
    }
  }, [jobId]);
  
  useEffect(() => {
    loadJob();
    loadContractors();
    loadCompletionPack();
    loadReviews();
    loadInvoice();
    loadActionHistory();
    loadNextAction();
    loadQuoteRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]); // Only re-run when jobId changes
  
  // ==========================================================================
  // ACTION HANDLERS
  // ==========================================================================
  
  // ==========================================================================
  // ACTION CENTRE HANDLERS - WhatsApp, Call, Outcome Logging
  // ==========================================================================
  
  const handleWhatsAppAction = async (templateKey, targetType = 'contractor') => {
    setActionLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('template_key', templateKey);
      params.append('target_type', targetType);
      
      const res = await axios.post(
        `${API}/api/fm/jobs/${jobId}/actions/whatsapp?${params.toString()}`,
        {},
        { headers }
      );
      
      if (res.data.ok && res.data.whatsapp_url) {
        // Open WhatsApp in new tab
        window.open(res.data.whatsapp_url, '_blank');
        toast.success(`WhatsApp opened for ${res.data.target?.name || targetType}`);
        
        // Set pending action for outcome capture
        setPendingActionId(res.data.action_id);
        setPendingActionType('whatsapp');
        setShowOutcomeSheet(true);
        
        // Refresh action history
        loadActionHistory();
      } else {
        toast.error(res.data.detail || 'Failed to create WhatsApp action');
      }
    } catch (err) {
      console.error('WhatsApp action error:', err);
      toast.error(err.response?.data?.detail || 'Failed to send WhatsApp');
    } finally {
      setActionLoading(false);
    }
  };
  
  const handleCallAction = async (targetType = 'contractor') => {
    setActionLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('target_type', targetType);
      
      const res = await axios.post(
        `${API}/api/fm/jobs/${jobId}/actions/call?${params.toString()}`,
        {},
        { headers }
      );
      
      if (res.data.ok && res.data.tel_url) {
        // Open tel link
        window.location.href = res.data.tel_url;
        toast.success(`Calling ${res.data.target?.name || targetType}...`);
        
        // Set pending action for outcome capture
        setPendingActionId(res.data.action_id);
        setPendingActionType('call');
        setShowOutcomeSheet(true);
        
        // Refresh action history
        loadActionHistory();
      } else {
        toast.error(res.data.detail || 'Failed to create call action');
      }
    } catch (err) {
      console.error('Call action error:', err);
      toast.error(err.response?.data?.detail || 'Failed to initiate call');
    } finally {
      setActionLoading(false);
    }
  };
  
  const handleLogOutcome = async () => {
    if (!outcomeStatus) {
      toast.error('Please select an outcome');
      return;
    }
    
    setActionLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('outcome_status', outcomeStatus);
      if (outcomeNote) params.append('outcome_note', outcomeNote);
      if (followUpDate) params.append('next_follow_up_at', followUpDate);
      
      await axios.post(
        `${API}/api/fm/jobs/${jobId}/actions/${pendingActionId}/outcome?${params.toString()}`,
        {},
        { headers }
      );
      
      toast.success('Outcome logged successfully');
      setShowOutcomeSheet(false);
      setOutcomeStatus('');
      setOutcomeNote('');
      setFollowUpDate('');
      setPendingActionId(null);
      setPendingActionType(null);
      loadActionHistory();
    } catch (err) {
      console.error('Log outcome error:', err);
      toast.error(err.response?.data?.detail || 'Failed to log outcome');
    } finally {
      setActionLoading(false);
    }
  };
  
  const handleQuickActionLog = async (actionType, details = null) => {
    setActionLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('action_type', actionType);
      if (details) params.append('details', details);
      
      const res = await axios.post(
        `${API}/api/fm/jobs/${jobId}/actions/log?${params.toString()}`,
        {},
        { headers }
      );
      
      toast.success(res.data.action_label || 'Action logged');
      loadActionHistory();
    } catch (err) {
      console.error('Quick action log error:', err);
      toast.error(err.response?.data?.detail || 'Failed to log action');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAssignContractor = async () => {
    if (!assignData.contractor_id) {
      toast.error('Please select a contractor');
      return;
    }
    
    try {
      const params = new URLSearchParams();
      params.append('contractor_id', assignData.contractor_id);
      if (assignData.scheduled_date) params.append('scheduled_date', assignData.scheduled_date);
      if (assignData.scheduled_time) params.append('scheduled_time', assignData.scheduled_time);
      
      await axios.post(`${API}/api/fm/jobs/${jobId}/assign-contractor?${params.toString()}`, {}, { headers });
      toast.success('Contractor assigned successfully');
      setShowAssignDialog(false);
      setAssignData({ contractor_id: '', scheduled_date: '', scheduled_time: '' });
      loadJob();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign contractor');
    }
  };
  
  const handleAddNote = async () => {
    if (!noteText.trim() && noteAttachments.length === 0) {
      toast.error('Please enter a note or add attachments');
      return;
    }
    
    setNoteUploading(true);
    try {
      if (noteAttachments.length > 0) {
        // Use multipart form for notes with attachments
        const formData = new FormData();
        formData.append('note', noteText.trim() || 'Attachment added');
        formData.append('note_type', 'internal');
        
        for (const att of noteAttachments) {
          if (att.file) {
            formData.append('files', att.file);
          }
        }
        
        await axios.post(`${API}/api/fm/jobs/${jobId}/note-with-files`, formData, {
          headers: { ...headers, 'Content-Type': 'multipart/form-data' }
        });
        toast.success(`Note added with ${noteAttachments.length} attachment(s)`);
      } else {
        // Simple text note
        await axios.post(`${API}/api/fm/jobs/${jobId}/add-note?note=${encodeURIComponent(noteText)}&note_type=internal`, {}, { headers });
        toast.success('Note added');
      }
      
      setShowNoteDialog(false);
      setNoteText('');
      setNoteAttachments([]);
      loadJob();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add note');
    } finally {
      setNoteUploading(false);
    }
  };
  
  const handleNoteFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const maxFiles = 10;
    
    if (noteAttachments.length + files.length > maxFiles) {
      toast.error(`Maximum ${maxFiles} files per note`);
      return;
    }
    
    const newAttachments = files.map(file => ({
      id: `temp_${Date.now()}_${Math.random()}`,
      name: file.name,
      size: file.size,
      type: file.type.startsWith('image/') ? 'image' : 'document',
      file: file,
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null
    }));
    
    setNoteAttachments(prev => [...prev, ...newAttachments]);
  };
  
  const removeNoteAttachment = (id) => {
    setNoteAttachments(prev => prev.filter(a => a.id !== id));
  };
  
  const handleSetRemedial = async () => {
    try {
      await axios.post(`${API}/api/fm/jobs/${jobId}/set-remedial?remedial_required=true`, {}, { headers });
      toast.success('Remedial flag set');
      loadJob();
    } catch (err) {
      toast.error('Failed to set remedial flag');
    }
  };
  
  const handleCreateRemedial = async () => {
    try {
      const res = await axios.post(`${API}/api/fm/jobs/${jobId}/create-remedial`, {}, { headers });
      toast.success('Remedial job created');
      navigate(`/fm/jobs/${res.data.remedial_job.id}`);
    } catch (err) {
      toast.error('Failed to create remedial job');
    }
  };
  
  const handleStartJob = async () => {
    try {
      await axios.patch(`${API}/api/fm/jobs/${jobId}/status?status=In Progress`, {}, { headers });
      toast.success('Job started');
      loadJob();
    } catch (err) {
      toast.error('Failed to start job');
    }
  };
  
  // --------------------------------------------------------------------------
  // COMPLETION PACK SUBMISSION
  // --------------------------------------------------------------------------
  const handleSubmitCompletion = async () => {
    // Validation
    if (!completionData.works_summary.trim()) {
      toast.error('Works summary is required');
      return;
    }
    if (!completionData.completion_notes.trim()) {
      toast.error('Completion notes are required');
      return;
    }
    if (completionData.after_photos.length === 0) {
      toast.error('At least one "After" photo is required');
      return;
    }
    
    try {
      await axios.post(`${API}/api/fm/jobs/${jobId}/completion-pack`, completionData, { headers });
      toast.success('Completion pack submitted - awaiting review');
      setShowCompletionDialog(false);
      setCompletionData({
        before_photos: [],
        during_photos: [],
        after_photos: [],
        completion_notes: '',
        works_summary: '',
        materials_used: '',
        compliance_documents: []
      });
      loadJob();
      loadCompletionPack();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit completion pack');
    }
  };
  
  // --------------------------------------------------------------------------
  // REVIEW GATE SUBMISSION
  // --------------------------------------------------------------------------
  const handleSubmitReview = async () => {
    if (reviewData.outcome !== 'Approve' && !reviewData.comments.trim()) {
      toast.error('Comments are required when rejecting or querying');
      return;
    }
    
    try {
      const res = await axios.post(`${API}/api/fm/jobs/${jobId}/review`, reviewData, { headers });
      toast.success(`Review submitted: ${res.data.new_status}`);
      setShowReviewDialog(false);
      setReviewData({
        outcome: 'Approve',
        comments: '',
        scope_aligned: true,
        satisfactory: true,
        evidence_complete: true,
        compliance_complete: true
      });
      loadJob();
      loadReviews();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit review');
    }
  };
  
  // --------------------------------------------------------------------------
  // INVOICE SUBMISSION
  // --------------------------------------------------------------------------
  const handleSubmitInvoice = async () => {
    if (!invoiceData.amount || parseFloat(invoiceData.amount) <= 0) {
      toast.error('Invoice amount is required');
      return;
    }
    
    try {
      const res = await axios.post(`${API}/api/fm/jobs/${jobId}/invoice`, {
        amount: parseFloat(invoiceData.amount),
        invoice_number: invoiceData.invoice_number,
        invoice_date: invoiceData.invoice_date,
        notes: invoiceData.notes
      }, { headers });
      
      if (res.data.has_issue) {
        toast.warning('Invoice Requires Review: ' + res.data.issues.join(', '));
      } else {
        toast.success('Invoice submitted and approved');
      }
      
      setShowInvoiceDialog(false);
      setInvoiceData({ amount: '', invoice_number: '', invoice_date: '', notes: '' });
      loadJob();
      loadInvoice();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit invoice');
    }
  };
  
  // --------------------------------------------------------------------------
  // QUOTE MANAGEMENT
  // --------------------------------------------------------------------------
  const handleAddQuoteLine = () => {
    setQuoteData(prev => ({
      ...prev,
      line_items: [...prev.line_items, { description: '', quantity: 1, unit_cost: 0 }]
    }));
  };
  
  const handleRemoveQuoteLine = (index) => {
    setQuoteData(prev => ({
      ...prev,
      line_items: prev.line_items.filter((_, i) => i !== index)
    }));
  };
  
  const handleUpdateQuoteLine = (index, field, value) => {
    setQuoteData(prev => ({
      ...prev,
      line_items: prev.line_items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };
  
  const calculateQuoteTotal = () => {
    return quoteData.line_items.reduce((sum, item) => {
      return sum + (item.quantity * item.unit_cost);
    }, 0);
  };
  
  const handleRejectQuote = async (quoteId) => {
    try {
      await axios.post(`${API}/api/quotes/${quoteId}/reject`, {}, { headers });
      toast.success('Quote rejected');
      loadJob();
    } catch (err) {
      toast.error('Failed to reject quote');
    }
  };
  
  // Select Quote with Auto-Close Engine
  const handleSelectQuoteWithAutoClose = async (quoteId) => {
    setSelectingQuote(true);
    try {
      const res = await axios.post(`${API}/api/lhs/jobs/${jobId}/select-quote`, {
        quote_id: quoteId
      }, { headers });
      
      if (res.data.success) {
        toast.success(`Quote approved! ${res.data.other_quotes_closed || 0} other quotes closed, ${res.data.requests_closed || 0} requests closed`);
        // Refresh all data
        loadJob();
        loadNextAction();
        loadQuoteRequests();
      } else {
        toast.error('Failed to approve quote');
      }
    } catch (err) {
      console.error('Select quote error:', err);
      toast.error(err.response?.data?.detail || 'Failed to approve quote');
    } finally {
      setSelectingQuote(false);
    }
  };
  
  // --------------------------------------------------------------------------
  // FILE UPLOAD HANDLERS (Simulated - would use actual file upload in production)
  // --------------------------------------------------------------------------
  const handleFileUpload = (category, files) => {
    // In production, this would upload files and return URLs
    // For now, we'll simulate with file names
    const fileNames = Array.from(files).map(f => f.name);
    setCompletionData(prev => ({
      ...prev,
      [category]: [...prev[category], ...fileNames]
    }));
    toast.success(`${fileNames.length} file(s) added to ${category.replace('_', ' ')}`);
  };
  
  const handleRemoveFile = (category, index) => {
    setCompletionData(prev => ({
      ...prev,
      [category]: prev[category].filter((_, i) => i !== index)
    }));
  };


  // ==========================================================================
  // SHARE TASK HANDLERS
  // ==========================================================================
  
  const handleDownloadPDF = async () => {
    try {
      const response = await axios.get(`${API}/api/fm/jobs/${jobId}/pdf`, {
        headers,
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `JobSheet_${job?.job_reference || jobId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded');
    } catch (err) {
      console.error('PDF download error:', err);
      toast.error('Failed to download PDF');
    }
  };
  
  const handleEmailPDF = async () => {
    if (!shareEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }
    
    setShareLoading(true);
    try {
      const res = await axios.post(
        `${API}/api/fm/jobs/${jobId}/email-pdf?recipient_email=${encodeURIComponent(shareEmail)}`,
        {},
        { headers }
      );
      
      if (res.data.ok) {
        toast.success(`Job sheet emailed to ${shareEmail}`);
        setShareEmail('');
        setShowShareDialog(false);
      }
    } catch (err) {
      console.error('Email error:', err);
      toast.error(err.response?.data?.detail || 'Failed to send email');
    } finally {
      setShareLoading(false);
    }
  };
  
  const handleWhatsAppShare = async () => {
    try {
      const res = await axios.get(
        `${API}/api/fm/jobs/${jobId}/whatsapp-link${sharePhone ? `?phone_number=${encodeURIComponent(sharePhone)}` : ''}`,
        { headers }
      );
      
      if (res.data.ok && res.data.whatsapp_url) {
        window.open(res.data.whatsapp_url, '_blank');
        setShowShareDialog(false);
      }
    } catch (err) {
      console.error('WhatsApp error:', err);
      toast.error('Failed to generate WhatsApp link');
    }
  };

  // ==========================================================================
  // FINAL OPERATIONAL PATCH HANDLERS
  // ==========================================================================
  
  // Check completion requirements (enforced)
  const handleCheckCompletion = async () => {
    setCompletionCheckLoading(true);
    try {
      const res = await axios.get(`${API}/api/fm/jobs/${jobId}/completion-check`, { headers });
      setCompletionCheck(res.data);
      setShowCompletionCheckDialog(true);
    } catch (err) {
      console.error('Completion check error:', err);
      toast.error(err.response?.data?.detail || 'Failed to check completion requirements');
    } finally {
      setCompletionCheckLoading(false);
    }
  };

  // Complete job (enforced - requires all documents)
  const handleCompleteEnforced = async () => {
    try {
      const res = await axios.post(`${API}/api/fm/jobs/${jobId}/complete-enforced`, {}, { headers });
      if (res.data.ok) {
        toast.success('Job completed successfully!');
        setShowCompletionCheckDialog(false);
        setCompletionCheck(null);
        loadJob();
      }
    } catch (err) {
      console.error('Complete enforced error:', err);
      const detail = err.response?.data?.detail;
      if (typeof detail === 'object' && detail.errors) {
        toast.error(
          <div>
            <strong>{detail.message}</strong>
            <ul className="mt-2 list-disc list-inside text-sm">
              {detail.errors.map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          </div>
        );
      } else {
        toast.error(detail || 'Failed to complete job');
      }
    }
  };

  // Upload completion documents
  const handleUploadCompletionDoc = async (fileType, file) => {
    if (!file) return;
    
    setUploadingDoc(true);
    try {
      const formData = new FormData();
      formData.append('file_type', fileType);
      formData.append('file', file);
      
      await axios.post(`${API}/api/fm/jobs/${jobId}/upload-completion`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(`${fileType.replace('_', ' ')} uploaded successfully`);
      loadJob();
      // Refresh completion check
      if (showCompletionCheckDialog) {
        handleCheckCompletion();
      }
    } catch (err) {
      console.error('Upload error:', err);
      toast.error(err.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploadingDoc(false);
    }
  };

  // Upload certificate/document
  const handleUploadDocument = async (docType, file, expiryDate = null) => {
    if (!file) return;
    
    setUploadingDoc(true);
    try {
      const formData = new FormData();
      formData.append('document_type', docType);
      formData.append('file', file);
      if (expiryDate) {
        formData.append('expiry_date', expiryDate);
      }
      
      await axios.post(`${API}/api/fm/jobs/${jobId}/upload-document`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(`${docType} uploaded successfully`);
      loadJob();
      setShowUploadDocsDialog(false);
    } catch (err) {
      console.error('Upload document error:', err);
      toast.error(err.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploadingDoc(false);
    }
  };

  // Create invoice from job (after completion + PO)
  const handleCreateInvoiceFromJob = async () => {
    setCreatingInvoice(true);
    try {
      const res = await axios.post(`${API}/api/fm/jobs/${jobId}/create-invoice`, {}, { headers });
      if (res.data.ok) {
        toast.success(`Invoice created: ${res.data.invoice.invoice_reference}`);
        setShowCreateInvoiceDialog(false);
        loadJob();
        loadInvoice();
      }
    } catch (err) {
      console.error('Create invoice error:', err);
      toast.error(err.response?.data?.detail || 'Failed to create invoice');
    } finally {
      setCreatingInvoice(false);
    }
  };

  // Approve quote (for staff)
  const handleApproveQuote = async (quoteId) => {
    setApprovingQuote(true);
    try {
      const res = await axios.post(`${API}/api/fm/quotes/${quoteId}/approve`, {}, { headers });
      if (res.data.ok) {
        toast.success(`Quote approved! PO: ${res.data.po_number}`);
        setShowApproveQuoteDialog(false);
        loadJob();
      }
    } catch (err) {
      console.error('Approve quote error:', err);
      toast.error(err.response?.data?.detail || 'Failed to approve quote');
    } finally {
      setApprovingQuote(false);
    }
  };

  // Share via new endpoints
  const handleShareEmail = async () => {
    if (!shareEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }
    
    setShareLoading(true);
    try {
      const res = await axios.post(
        `${API}/api/fm/jobs/${jobId}/share/email?to_email=${encodeURIComponent(shareEmail)}`,
        {},
        { headers }
      );
      
      if (res.data.ok) {
        toast.success(`Job sheet emailed to ${shareEmail}`);
        setShareEmail('');
        setShowShareDialog(false);
      }
    } catch (err) {
      console.error('Email error:', err);
      toast.error(err.response?.data?.detail || 'Failed to send email');
    } finally {
      setShareLoading(false);
    }
  };

  const handleShareWhatsApp = async () => {
    try {
      const res = await axios.get(`${API}/api/fm/jobs/${jobId}/share/whatsapp`, { headers });
      
      if (res.data.ok && res.data.whatsapp_url) {
        window.open(res.data.whatsapp_url, '_blank');
        setShowShareDialog(false);
      }
    } catch (err) {
      console.error('WhatsApp error:', err);
      toast.error('Failed to generate WhatsApp link');
    }
  };

  
  // ==========================================================================
  // RENDER LOADING/ERROR STATES
  // ==========================================================================
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1F7A78]"></div>
      </div>
    );
  }
  
  if (!job) {
    // Check if this looks like a PPM schedule ID (no linked job yet)
    const isPPMScheduleId = jobId?.startsWith('ppm_');
    
    return (
      <div className="p-6 text-center">
        <AlertTriangle className="h-12 w-12 mx-auto text-amber-500 mb-4" />
        <h2 className="text-xl font-semibold mb-2">Job Not Found</h2>
        {isPPMScheduleId ? (
          <div className="space-y-3">
            <p className="text-slate-500 max-w-md mx-auto">
              This PPM task doesn't have a linked job yet. A job will be created when the task is assigned to a contractor or scheduled.
            </p>
            <div className="flex gap-3 justify-center">
              <Button onClick={() => navigate('/ppm-schedule')} variant="outline">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to PPM Schedule
              </Button>
              <Button onClick={() => navigate('/fm')} className="bg-[#1F7A78] hover:bg-[#16615f]">
                View All Jobs
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-slate-500">The job you're looking for doesn't exist or has been removed.</p>
            <Button onClick={() => navigate('/fm')} variant="outline">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to FM Dashboard
            </Button>
          </div>
        )}
      </div>
    );
  }
  
  // ==========================================================================
  // DETERMINE AVAILABLE ACTIONS BASED ON STATUS
  // ==========================================================================
  const canAssign = ['New', 'Unassigned'].includes(job.status);
  const canSchedule = job.status === 'Assigned' && job.assigned_contractor_id;
  const canStart = job.status === 'Scheduled';
  const canComplete = job.status === 'In Progress';
  const canReview = job.status === 'Completed – Awaiting Review';
  const canInvoice = job.status === 'Approved for Invoicing';
  const hasInvoiceIssue = job.has_invoice_issue;
  
  // ==========================================================================
  // MAIN RENDER
  // ==========================================================================
  return (
    <div className="p-4 sm:p-6 space-y-4">
      {/* Back Button */}
      <div className="flex items-center gap-3 mb-2">
        <Button variant="ghost" size="sm" onClick={() => navigate('/fm')} data-testid="back-btn">
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to Jobs
        </Button>
      </div>

      {/* Next Action Banner - Control Engine Intelligence */}
      {nextActionData && (
        <NextActionBanner 
          nextAction={nextActionData.next_action || job.next_action}
          status={nextActionData.status || job.status}
          reassignCount={nextActionData.reassign_count || 0}
        />
      )}

      {/* Unified JobCard - Main Job Display */}
      <JobCard
        job={mapJobToUnified(job)}
        variant="internal"
        actions={{
          onAssign: (canAssign || job.assigned_contractor_id) ? () => setShowAssignDialog(true) : undefined,
          onApprove: job.quotes?.some(q => q.status === 'Pending') ? () => setShowApproveQuoteDialog(true) : undefined,
          onInvoice: (job.completed_at || job.status === 'Completed') && job.po_number && !job.invoice_id ? () => setShowCreateInvoiceDialog(true) : undefined,
          onDownloadPdf: () => window.open(`${API}/api/fm/jobs/${job.id || job._id}/job-sheet.pdf`, '_blank'),
        }}
        footer={
          <div className="flex flex-wrap gap-2">
            {/* Additional Status Badges */}
            {job.source_type && (
              <Badge variant="outline">{job.source_type}</Badge>
            )}
            {job.is_legal_requirement && (
              <Badge className="bg-red-100 text-red-700">Legal</Badge>
            )}
            {job.remedial_required && (
              <Badge className="bg-amber-100 text-amber-700 flex items-center gap-1">
                <AlertOctagon className="h-3 w-3" />
                Remedials Required
              </Badge>
            )}
            {hasInvoiceIssue && (
              <Badge className="bg-orange-100 text-orange-700 flex items-center gap-1">
                <Receipt className="h-3 w-3" />
                Invoice Requires Review
              </Badge>
            )}
          </div>
        }
      />

      {/* Operational Actions Bar */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex flex-wrap items-center gap-4 sm:gap-6">
              <div>
                <span className="text-sm text-slate-500 flex items-center gap-1">
                  <User className="h-3 w-3" /> Contractor
                </span>
                <p className="font-medium">{job.assigned_contractor_name || 'Not Assigned'}</p>
              </div>
              <div>
                <span className="text-sm text-slate-500 flex items-center gap-1">
                  <Calendar className="h-3 w-3" /> Scheduled
                </span>
                <p className="font-medium">
                  {job.scheduled_date ? format(parseISO(job.scheduled_date), 'dd MMM yyyy') : 'Not Scheduled'}
                  {job.scheduled_time && ` at ${job.scheduled_time}`}
                </p>
              </div>
              {job.po_number && (
                <div>
                  <span className="text-sm text-slate-500 flex items-center gap-1">
                    <FileText className="h-3 w-3" /> PO Number
                  </span>
                  <p className="font-medium text-[#1F7A78]">{job.po_number}</p>
                </div>
              )}
            </div>
            
            {/* Action Buttons */}
            <div className="flex flex-wrap gap-2">
              {(canAssign || job.assigned_contractor_id) && (
                <Button 
                  size="sm" 
                  className="bg-[#1F7A78] hover:bg-[#16615f]"
                  onClick={() => setShowAssignDialog(true)}
                  data-testid="assign-contractor-btn"
                >
                  <UserPlus className="h-4 w-4 mr-1" />
                  {job.assigned_contractor_id ? 'Reassign' : 'Assign'}
                </Button>
              )}
              {canStart && (
                <Button size="sm" variant="outline" onClick={handleStartJob} data-testid="start-job-btn">
                  Start Job
                </Button>
              )}
              
              {/* Upload Documents Button */}
              {['In Progress', 'Scheduled', 'Assigned', 'PO Issued'].includes(job.status) && (
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => setShowUploadDocsDialog(true)}
                  data-testid="upload-docs-btn"
                >
                  <Upload className="h-4 w-4 mr-1" />
                  Upload Docs
                </Button>
              )}
              
              {/* Complete Job */}
              {['In Progress', 'Scheduled', 'Assigned', 'PO Issued'].includes(job.status) && !job.completed_at && (
                <Button 
                  size="sm" 
                  className="bg-green-600 hover:bg-green-700" 
                  onClick={handleCheckCompletion}
                  disabled={completionCheckLoading}
                  data-testid="complete-enforced-btn"
                >
                  {completionCheckLoading ? (
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-1" />
                  ) : (
                    <CheckCircle className="h-4 w-4 mr-1" />
                  )}
                  Complete Job
                </Button>
              )}
              
              {canComplete && (
                <Button 
                  size="sm" 
                  className="bg-green-600 hover:bg-green-700" 
                  onClick={() => setShowCompletionDialog(true)}
                  data-testid="complete-job-btn"
                >
                  <CheckCircle className="h-4 w-4 mr-1" />
                  Submit Completion
                </Button>
              )}
              
              {/* Approve Quote */}
              {job.quotes?.some(q => q.status === 'Pending') && (
                <Button 
                  size="sm" 
                  className="bg-purple-600 hover:bg-purple-700"
                  onClick={() => setShowApproveQuoteDialog(true)}
                  data-testid="approve-quote-btn"
                >
                  <FileCheck className="h-4 w-4 mr-1" />
                  Approve Quote
                </Button>
              )}
              
              {canReview && (
                <Button 
                  size="sm" 
                  className="bg-amber-500 hover:bg-amber-600"
                  onClick={() => setShowReviewDialog(true)}
                  data-testid="review-job-btn"
                >
                  <ClipboardCheck className="h-4 w-4 mr-1" />
                  Review
                </Button>
              )}
              
              {/* Create Invoice */}
              {(job.completed_at || job.status === 'Completed') && job.po_number && !job.invoice_id && (
                <Button 
                  size="sm" 
                  className="bg-blue-600 hover:bg-blue-700"
                  onClick={() => setShowCreateInvoiceDialog(true)}
                  data-testid="create-invoice-btn"
                >
                  <Receipt className="h-4 w-4 mr-1" />
                  Create Invoice
                </Button>
              )}
              
              {canInvoice && (
                <Button 
                  size="sm" 
                  className="bg-blue-600 hover:bg-blue-700"
                  onClick={() => {
                    // Pre-fill with PO value
                    const poValue = job.approved_quote?.total_cost || job.approved_quote?.total || 0;
                    setInvoiceData(prev => ({ ...prev, amount: poValue.toString() }));
                    setShowInvoiceDialog(true);
                  }}
                  data-testid="submit-invoice-btn"
                >
                  <Receipt className="h-4 w-4 mr-1" />
                  Submit Invoice
                </Button>
              )}
              
              {/* Share Task Button - always visible */}
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => setShowShareDialog(true)}
                data-testid="share-task-btn"
              >
                <Share2 className="h-4 w-4 mr-1" />
                Share Task
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* ================================================================
          ACTION CENTRE - WhatsApp / Call / Quick Actions
      ================================================================ */}
      <Card className="border-0 shadow-sm bg-gradient-to-r from-[#1F7A78]/5 to-emerald-50/50">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-[#1F7A78] flex items-center gap-2">
              <MessageCircle className="h-4 w-4" />
              Action Centre
            </h3>
            {actionHistory.length > 0 && (
              <span className="text-xs text-slate-500">
                Last: {actionHistory[0]?.action_label} • {actionHistory[0]?.created_at ? format(parseISO(actionHistory[0].created_at), 'dd MMM HH:mm') : ''}
              </span>
            )}
          </div>
          
          {/* Primary Actions Row */}
          <div className="flex flex-wrap gap-2 mb-3">
            {/* WhatsApp Contractor */}
            <Button
              size="sm"
              variant="outline"
              className="bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
              onClick={() => handleWhatsAppAction('contractor_job_chase', 'contractor')}
              disabled={actionLoading || !job.assigned_contractor_id}
              data-testid="whatsapp-contractor-btn"
            >
              <MessageCircle className="h-4 w-4 mr-1" />
              WhatsApp Contractor
            </Button>
            
            {/* Call Contractor */}
            <Button
              size="sm"
              variant="outline"
              className="bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100"
              onClick={() => handleCallAction('contractor')}
              disabled={actionLoading || !job.assigned_contractor_id}
              data-testid="call-contractor-btn"
            >
              <PhoneCall className="h-4 w-4 mr-1" />
              Call Contractor
            </Button>
            
            {/* Request Quote */}
            {['Assigned', 'Job Created', 'Awaiting Quote'].includes(job.status) && (
              <Button
                size="sm"
                variant="outline"
                className="bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100"
                onClick={() => handleWhatsAppAction('contractor_quote_request', 'contractor')}
                disabled={actionLoading || !job.assigned_contractor_id}
                data-testid="request-quote-btn"
              >
                <FileText className="h-4 w-4 mr-1" />
                Request Quote
              </Button>
            )}
            
            {/* Confirm Attendance */}
            {job.status === 'Scheduled' && (
              <Button
                size="sm"
                variant="outline"
                className="bg-teal-50 border-teal-200 text-teal-700 hover:bg-teal-100"
                onClick={() => handleWhatsAppAction('attendance_confirmation', 'contractor')}
                disabled={actionLoading || !job.assigned_contractor_id}
                data-testid="confirm-attendance-btn"
              >
                <Calendar className="h-4 w-4 mr-1" />
                Confirm Attendance
              </Button>
            )}
          </div>
          
          {/* Secondary Actions Row */}
          <div className="flex flex-wrap gap-2">
            {/* Mark No Response */}
            <Button
              size="sm"
              variant="ghost"
              className="text-amber-600 hover:bg-amber-50"
              onClick={() => handleQuickActionLog('no_response', 'No response from contractor')}
              disabled={actionLoading}
              data-testid="mark-no-response-btn"
            >
              <XCircle className="h-4 w-4 mr-1" />
              No Response
            </Button>
            
            {/* Escalate */}
            <Button
              size="sm"
              variant="ghost"
              className="text-red-600 hover:bg-red-50"
              onClick={() => handleQuickActionLog('escalated', 'Job escalated due to non-response')}
              disabled={actionLoading}
              data-testid="escalate-btn"
            >
              <AlertTriangle className="h-4 w-4 mr-1" />
              Escalate
            </Button>
            
            {/* Reassign */}
            <Button
              size="sm"
              variant="ghost"
              className="text-slate-600 hover:bg-slate-50"
              onClick={() => {
                handleQuickActionLog('reassign_required', 'Contractor reassignment requested');
                setShowAssignDialog(true);
              }}
              disabled={actionLoading}
              data-testid="reassign-btn"
            >
              <RefreshCw className="h-4 w-4 mr-1" />
              Reassign
            </Button>
            
            {/* Client Update */}
            <Button
              size="sm"
              variant="ghost"
              className="text-indigo-600 hover:bg-indigo-50"
              onClick={() => handleWhatsAppAction('client_update', 'client')}
              disabled={actionLoading}
              data-testid="client-update-btn"
            >
              <Send className="h-4 w-4 mr-1" />
              Update Client
            </Button>
          </div>
        </CardContent>
      </Card>
      
      {/* ================================================================
          SPECIAL ACTIONS - Chargeable / Project / Capex
      ================================================================ */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <h3 className="font-semibold text-slate-700 mb-3">Special Actions</h3>
          <JobActionButtons job={job} onUpdate={loadJob} />
        </CardContent>
      </Card>
      
      {/* Quick Outcome Sheet - Inline Panel */}
      {showOutcomeSheet && (
        <Card className="border-0 shadow-sm border-l-4 border-l-[#1F7A78]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-[#1F7A78]">
                Log Outcome ({pendingActionType === 'whatsapp' ? 'WhatsApp' : 'Call'})
              </h4>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setShowOutcomeSheet(false);
                  setPendingActionId(null);
                  setPendingActionType(null);
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            
            <div className="grid gap-4 sm:grid-cols-2">
              {/* Outcome Selection */}
              <div>
                <Label className="text-slate-500 mb-2 block">Outcome *</Label>
                <div className="flex flex-wrap gap-2">
                  {[
                    { value: 'confirmed', label: 'Confirmed', color: 'emerald' },
                    { value: 'no_answer', label: 'No Answer', color: 'amber' },
                    { value: 'quote_promised', label: 'Quote Promised', color: 'purple' },
                    { value: 'engineer_assigned', label: 'Engineer Assigned', color: 'blue' },
                    { value: 'delayed', label: 'Delayed', color: 'orange' },
                    { value: 'callback_required', label: 'Callback Required', color: 'cyan' },
                    { value: 'escalate', label: 'Escalate', color: 'red' },
                  ].map(opt => (
                    <Button
                      key={opt.value}
                      size="sm"
                      variant={outcomeStatus === opt.value ? 'default' : 'outline'}
                      className={outcomeStatus === opt.value ? `bg-${opt.color}-600` : ''}
                      onClick={() => setOutcomeStatus(opt.value)}
                      data-testid={`outcome-${opt.value}-btn`}
                    >
                      {opt.label}
                    </Button>
                  ))}
                </div>
              </div>
              
              {/* Follow-up Date */}
              <div>
                <Label className="text-slate-500 mb-2 block">Follow-up Date (optional)</Label>
                <Input
                  type="datetime-local"
                  value={followUpDate}
                  onChange={(e) => setFollowUpDate(e.target.value)}
                  className="w-full"
                  data-testid="followup-date-input"
                />
              </div>
            </div>
            
            {/* Quick Note */}
            <div className="mt-3">
              <Label className="text-slate-500 mb-2 block">Quick Note (optional)</Label>
              <Input
                value={outcomeNote}
                onChange={(e) => setOutcomeNote(e.target.value)}
                placeholder="e.g., Engineer arriving 2pm tomorrow"
                className="w-full"
                data-testid="outcome-note-input"
              />
            </div>
            
            {/* Save Button */}
            <div className="mt-4 flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowOutcomeSheet(false);
                  setPendingActionId(null);
                  setPendingActionType(null);
                }}
              >
                Skip
              </Button>
              <Button
                className="bg-[#1F7A78] hover:bg-[#16615f]"
                onClick={handleLogOutcome}
                disabled={actionLoading || !outcomeStatus}
                data-testid="save-outcome-btn"
              >
                {actionLoading ? (
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-1" />
                ) : (
                  <CheckCircle className="h-4 w-4 mr-1" />
                )}
                Save Outcome
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="w-full flex overflow-x-auto hide-scrollbar">
          <TabsTrigger value="summary" className="flex-1 min-w-[80px]">Summary</TabsTrigger>
          <TabsTrigger value="quotes" className="flex-1 min-w-[80px]">Quotes</TabsTrigger>
          <TabsTrigger value="works" className="flex-1 min-w-[80px]">Works</TabsTrigger>
          <TabsTrigger value="inspections" className="flex-1 min-w-[80px]">Inspections</TabsTrigger>
          <TabsTrigger value="attachments" className="flex-1 min-w-[80px]">Attachments</TabsTrigger>
          <TabsTrigger value="financials" className="flex-1 min-w-[80px]">Financials</TabsTrigger>
          <TabsTrigger value="history" className="flex-1 min-w-[80px]">History</TabsTrigger>
        </TabsList>
        
        {/* ================================================================
            SUMMARY TAB
        ================================================================ */}
        <TabsContent value="summary" className="mt-4">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4 space-y-4">
              <div>
                <Label className="text-slate-500">Description</Label>
                <p className="mt-1">{job.description || job.full_description || job.short_description || 'No description'}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-500">Job Type</Label>
                  <p className="font-medium">{job.job_type_label || job.job_type}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Source</Label>
                  <p className="font-medium">{job.source_type || 'Manual'}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Created</Label>
                  <p className="font-medium">
                    {job.created_at ? format(parseISO(job.created_at), 'dd MMM yyyy HH:mm') : '-'}
                  </p>
                </div>
                <div>
                  <Label className="text-slate-500">Completed</Label>
                  <p className="font-medium">
                    {job.completed_at ? format(parseISO(job.completed_at), 'dd MMM yyyy HH:mm') : '-'}
                  </p>
                </div>
              </div>
              
              {/* Linked PPM Info */}
              {job.linked_ppm && (
                <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                  <Label className="text-purple-700">Linked PPM Schedule</Label>
                  <p className="text-sm">{job.linked_ppm.task_name_snapshot}</p>
                  <p className="text-xs text-purple-600">
                    Next Due: {job.linked_ppm.next_due_date ? format(parseISO(job.linked_ppm.next_due_date), 'dd MMM yyyy') : '-'}
                  </p>
                </div>
              )}
              
              {/* Uplift Requests Panel */}
              {(job.uplifts?.length > 0 || job.has_pending_uplift) && (
                <UpliftPanel 
                  job={job} 
                  token={localStorage.getItem('token')} 
                  onRefresh={fetchJob} 
                />
              )}
              
              {/* Completion Pack Status */}
              {completionPack && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <FileCheck className="h-4 w-4 text-green-600" />
                    <Label className="text-green-700">Completion Pack Submitted</Label>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div>Before: {completionPack.before_photos?.length || 0} photos</div>
                    <div>During: {completionPack.during_photos?.length || 0} photos</div>
                    <div>After: {completionPack.after_photos?.length || 0} photos</div>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Submitted: {completionPack.submitted_at ? format(parseISO(completionPack.submitted_at), 'dd MMM yyyy HH:mm') : '-'}
                  </p>
                </div>
              )}
              
              {/* Review Status */}
              {reviews.length > 0 && (
                <div className={`p-3 rounded-lg border ${
                  reviews[0].outcome === 'Approve' ? 'bg-green-50 border-green-200' :
                  reviews[0].outcome === 'Reject' ? 'bg-red-50 border-red-200' :
                  'bg-orange-50 border-orange-200'
                }`}>
                  <div className="flex items-center gap-2 mb-2">
                    <ClipboardCheck className={`h-4 w-4 ${
                      reviews[0].outcome === 'Approve' ? 'text-green-600' :
                      reviews[0].outcome === 'Reject' ? 'text-red-600' :
                      'text-orange-600'
                    }`} />
                    <Label className={
                      reviews[0].outcome === 'Approve' ? 'text-green-700' :
                      reviews[0].outcome === 'Reject' ? 'text-red-700' :
                      'text-orange-700'
                    }>
                      Review: {reviews[0].outcome}
                    </Label>
                  </div>
                  {reviews[0].comments && (
                    <p className="text-sm">{reviews[0].comments}</p>
                  )}
                  <p className="text-xs text-slate-500 mt-1">
                    By: {reviews[0].reviewer} - {reviews[0].created_at ? format(parseISO(reviews[0].created_at), 'dd MMM yyyy HH:mm') : '-'}
                  </p>
                </div>
              )}
              
              {/* Notes */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-slate-500">Internal Notes</Label>
                </div>
                
                {/* Quick Upload Buttons - Visible directly */}
                <div className="flex gap-2 mb-3">
                  <Button size="sm" variant="outline" onClick={() => setShowNoteDialog(true)} className="bg-white">
                    <Plus className="h-3 w-3 mr-1" />
                    Add Note
                  </Button>
                  <label className="cursor-pointer">
                    <input 
                      type="file" 
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={(e) => {
                        if (e.target.files?.length) {
                          // Pre-populate note dialog with files
                          const files = Array.from(e.target.files).slice(0, 10);
                          const newAttachments = files.map(f => ({
                            id: `att_${Date.now()}_${Math.random().toString(36).substr(2,9)}`,
                            file: f,
                            filename: f.name,
                            type: f.type.startsWith('image/') ? 'image' : 'document',
                            preview: f.type.startsWith('image/') ? URL.createObjectURL(f) : null
                          }));
                          setNoteAttachments(newAttachments);
                          setShowNoteDialog(true);
                        }
                      }}
                      data-testid="quick-photo-input"
                    />
                    <Button type="button" variant="outline" size="sm" asChild className="bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100">
                      <span><Camera className="h-3 w-3 mr-1" />Photo</span>
                    </Button>
                  </label>
                  <label className="cursor-pointer">
                    <input 
                      type="file" 
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.txt"
                      multiple
                      className="hidden"
                      onChange={(e) => {
                        if (e.target.files?.length) {
                          const files = Array.from(e.target.files).slice(0, 10);
                          const newAttachments = files.map(f => ({
                            id: `att_${Date.now()}_${Math.random().toString(36).substr(2,9)}`,
                            file: f,
                            filename: f.name,
                            type: 'document',
                            preview: null
                          }));
                          setNoteAttachments(newAttachments);
                          setShowNoteDialog(true);
                        }
                      }}
                      data-testid="quick-document-input"
                    />
                    <Button type="button" variant="outline" size="sm" asChild className="bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100">
                      <span><FileText className="h-3 w-3 mr-1" />Document</span>
                    </Button>
                  </label>
                </div>
                
                <div className="space-y-2">
                  {safeArray(job.notes).length === 0 ? (
                    <p className="text-sm text-slate-400">No notes</p>
                  ) : (
                    safeArray(job.notes).map((note, idx) => (
                      <div key={note.id || idx} className="bg-slate-50 p-3 rounded-lg text-sm border">
                        <p>{typeof note.text === 'object' ? JSON.stringify(note.text) : (note.text || note.note || note.content || '')}</p>
                        
                        {/* Note Attachments */}
                        {(note.attachments || []).length > 0 && (
                          <div className="mt-2 pt-2 border-t border-slate-200">
                            <div className="flex items-center gap-1 text-xs text-slate-500 mb-2">
                              <Paperclip className="h-3 w-3" />
                              {note.attachments.length} attachment(s)
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {note.attachments.map((att, attIdx) => (
                                <div 
                                  key={att.id || attIdx} 
                                  className="flex items-center gap-1 bg-white px-2 py-1 rounded border text-xs cursor-pointer hover:bg-slate-100"
                                  title={typeof att === 'string' ? att : (att.original_filename || att.filename || 'File')}
                                >
                                  {(att.type === 'image' || (att.content_type && att.content_type.startsWith('image/'))) ? (
                                    <Image className="h-3 w-3 text-blue-500" />
                                  ) : (
                                    <FileText className="h-3 w-3 text-amber-500" />
                                  )}
                                  <span className="max-w-[100px] truncate">
                                    {typeof att === 'string' ? att : (att.original_filename || att.filename || 'File')}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        <p className="text-xs text-slate-400 mt-2">
                          {note.created_at && format(parseISO(note.created_at), 'dd MMM yyyy HH:mm')} - {note.created_by || 'System'}
                        </p>
                      </div>
                    ))
                  )}
                </div>
              </div>
              
              {/* Remedial Section */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-slate-500">Remedial Works</Label>
                    {job.remedial_required ? (
                      <p className="text-amber-600 font-medium">Remedials Required</p>
                    ) : (
                      <p className="text-slate-400">No remedials flagged</p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {!job.remedial_required && (
                      <Button size="sm" variant="outline" onClick={handleSetRemedial}>
                        <AlertOctagon className="h-3 w-3 mr-1" />
                        Flag Remedial
                      </Button>
                    )}
                    {job.remedial_required && (
                      <Button size="sm" className="bg-amber-500 hover:bg-amber-600" onClick={handleCreateRemedial}>
                        <Plus className="h-3 w-3 mr-1" />
                        Create Remedial Job
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* ================================================================
            QUOTES TAB
        ================================================================ */}
        <TabsContent value="quotes" className="mt-4">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Quotes</CardTitle>
                <Button size="sm" className="bg-[#1F7A78] hover:bg-[#16615f]" onClick={() => setShowQuoteDialog(true)}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Quote
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Quote Requests Section */}
              {quoteRequests.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-slate-600 mb-3 flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Quote Requests ({quoteRequests.length})
                  </h4>
                  <div className="space-y-2">
                    {quoteRequests.map((qr) => (
                      <div key={qr._id} className={`flex items-center justify-between p-3 rounded-lg border ${
                        qr.status === 'DECLINED' || qr.status === 'EXPIRED' ? 'bg-red-50 border-red-200' :
                        qr.status === 'SUBMITTED' ? 'bg-green-50 border-green-200' :
                        qr.status === 'CLOSED_NOT_REQUIRED' ? 'bg-slate-50 border-slate-200' :
                        'bg-amber-50 border-amber-200'
                      }`}>
                        <div>
                          <p className="font-medium text-sm">{qr.contractor_name || 'Contractor'}</p>
                          <p className="text-xs text-slate-500">
                            Requested: {qr.created_at && format(parseISO(qr.created_at), 'dd MMM yyyy HH:mm')}
                          </p>
                          {qr.decline_reason && (
                            <p className="text-xs text-red-600 mt-1">Reason: {qr.decline_reason}</p>
                          )}
                          {qr.closed_reason && (
                            <p className="text-xs text-slate-500 mt-1">Closed: {qr.closed_reason}</p>
                          )}
                        </div>
                        <Badge className={
                          qr.status === 'DECLINED' ? 'bg-red-100 text-red-700' :
                          qr.status === 'EXPIRED' ? 'bg-red-100 text-red-700' :
                          qr.status === 'SUBMITTED' ? 'bg-green-100 text-green-700' :
                          qr.status === 'CLOSED_NOT_REQUIRED' ? 'bg-slate-100 text-slate-600' :
                          'bg-amber-100 text-amber-700'
                        }>
                          {qr.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Submitted Quotes Section */}
              {safeArray(job.quotes).length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No quotes submitted</p>
                  <p className="text-xs mt-1">NO QUOTE BREAKDOWN = REJECT</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {safeArray(job.quotes).map((quote, idx) => (
                    <div key={quote.id || quote._id || idx} className={`border rounded-lg p-4 ${
                      quote.status === 'Accepted' || quote.status === 'Approved' || quote.status === 'APPROVED' 
                        ? 'border-green-300 bg-green-50' 
                        : quote.status === 'NOT_SELECTED' || quote.status === 'Rejected'
                        ? 'border-red-200 bg-red-50'
                        : ''
                    }`}>
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <p className="font-medium">{quote.contractor_name || 'Contractor'}</p>
                          <p className="text-xs text-slate-500">
                            {quote.submitted_at && format(parseISO(quote.submitted_at), 'dd MMM yyyy')}
                            {quote.created_at && !quote.submitted_at && format(parseISO(quote.created_at), 'dd MMM yyyy')}
                          </p>
                        </div>
                        <Badge className={
                          quote.status === 'Accepted' || quote.status === 'Approved' || quote.status === 'APPROVED'
                            ? 'bg-green-100 text-green-700' 
                            : quote.status === 'Rejected' || quote.status === 'NOT_SELECTED'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-amber-100 text-amber-700'
                        }>
                          {quote.status || 'Pending'}
                        </Badge>
                      </div>
                      
                      {/* Quote Breakdown */}
                      {quote.line_items && quote.line_items.length > 0 && (
                        <div className="border-t pt-3 space-y-1">
                          {quote.line_items.map((item, i) => (
                            <div key={i} className="flex justify-between text-sm">
                              <span>{item.description} (x{item.quantity})</span>
                              <span className="font-medium">£{(item.total || item.quantity * item.unit_cost || 0).toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Legacy cost breakdown */}
                      {!quote.line_items && (quote.labour_cost || quote.materials_cost || quote.labour || quote.materials) && (
                        <div className="border-t pt-3 space-y-1 text-sm">
                          {(quote.labour_cost > 0 || quote.labour > 0) && (
                            <div className="flex justify-between">
                              <span>Labour</span>
                              <span>£{(quote.labour_cost || quote.labour || 0).toFixed(2)}</span>
                            </div>
                          )}
                          {(quote.materials_cost > 0 || quote.materials > 0) && (
                            <div className="flex justify-between">
                              <span>Materials</span>
                              <span>£{(quote.materials_cost || quote.materials || 0).toFixed(2)}</span>
                            </div>
                          )}
                        </div>
                      )}
                      
                      <div className="border-t mt-3 pt-3 flex justify-between font-medium">
                        <span>Total</span>
                        <span>£{(quote.total_cost || quote.total || 0).toFixed(2)}</span>
                      </div>
                      
                      {/* Action Buttons - Only for non-finalized quotes */}
                      {!['Accepted', 'Approved', 'APPROVED', 'Rejected', 'NOT_SELECTED'].includes(quote.status) && (
                        <div className="flex gap-2 mt-3">
                          <Button 
                            size="sm" 
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => handleSelectQuoteWithAutoClose(quote.id || quote._id)}
                            disabled={selectingQuote}
                            data-testid={`approve-quote-${quote.id || quote._id}`}
                          >
                            {selectingQuote ? (
                              <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                            ) : (
                              <CheckCircle className="h-4 w-4 mr-1" />
                            )}
                            Approve & Close Others
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="text-red-600"
                            onClick={() => handleRejectQuote(quote.id || quote._id)}
                            data-testid={`reject-quote-${quote.id || quote._id}`}
                          >
                            Reject
                          </Button>
                        </div>
                      )}
                      
                      {/* Closed reason display */}
                      {quote.closed_reason && (
                        <p className="text-xs text-slate-500 mt-2 italic">
                          Closed: {quote.closed_reason}
                        </p>
                      )}
                      
                      {(quote.status === 'Accepted' || quote.status === 'Approved' || quote.status === 'APPROVED') && job.po_number && (
                        <div className="mt-3 p-2 bg-green-100 rounded text-sm text-green-700">
                          PO Generated: <strong>{job.po_number}</strong>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* ================================================================
            WORKS/UPDATES TAB
        ================================================================ */}
        <TabsContent value="works" className="mt-4">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-base">Works & Updates</CardTitle>
            </CardHeader>
            <CardContent>
              {/* Completion Pack Summary if exists */}
              {completionPack && (
                <div className="mb-4 p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <FileCheck className="h-4 w-4 text-green-600" />
                    Completion Pack
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div>
                      <Label className="text-slate-500">Works Summary</Label>
                      <p>{completionPack.works_summary || '-'}</p>
                    </div>
                    <div>
                      <Label className="text-slate-500">Completion Notes</Label>
                      <p>{completionPack.completion_notes || '-'}</p>
                    </div>
                    <div>
                      <Label className="text-slate-500">Materials Used</Label>
                      <p>{completionPack.materials_used || '-'}</p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Work Updates Timeline */}
              <div className="space-y-4">
                {safeArray(job.notes).filter(n => n.type === 'contractor' || n.type === 'site' || n.type === 'works').length === 0 && !completionPack ? (
                  <div className="text-center py-8 text-slate-500">
                    <MessageSquare className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No work updates yet</p>
                  </div>
                ) : (
                  safeArray(job.notes).filter(n => n.type === 'contractor' || n.type === 'site' || n.type === 'works').map((note, idx) => (
                    <div key={idx} className="border-l-4 border-[#1F7A78] pl-4 py-2">
                      <p className="font-medium text-sm">{note.type === 'contractor' ? 'Contractor Update' : 'Site Note'}</p>
                      <p className="text-sm">{typeof note.text === 'object' ? JSON.stringify(note.text) : (note.text || note.note || note.content || '')}</p>
                      <p className="text-xs text-slate-400 mt-1">
                        {note.created_at && format(parseISO(note.created_at), 'dd MMM yyyy HH:mm')}
                      </p>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* ================================================================
            INSPECTIONS TAB
        ================================================================ */}
        <TabsContent value="inspections" className="mt-4">
          <InspectionsTab 
            jobId={job.id || job.internal_id || id} 
            jobRef={job.job_reference}
            siteName={job.site_name}
            plot={job.plot || job.plot_number || job.unit}
          />
        </TabsContent>
        
        {/* ================================================================
            ATTACHMENTS TAB
        ================================================================ */}
        <TabsContent value="attachments" className="mt-4">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Attachments</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              {/* Completion Pack Photos */}
              {completionPack && (
                <div className="space-y-4 mb-6">
                  {/* Before Photos */}
                  <div>
                    <Label className="flex items-center gap-2 mb-2">
                      <Camera className="h-4 w-4" /> Before Photos ({completionPack.before_photos?.length || 0})
                    </Label>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {(completionPack.before_photos || []).map((photo, idx) => (
                        <div key={idx} className="aspect-square bg-slate-100 rounded flex items-center justify-center">
                          <Image className="h-8 w-8 text-slate-400" />
                        </div>
                      ))}
                      {(completionPack.before_photos || []).length === 0 && (
                        <p className="text-sm text-slate-400 col-span-full">No before photos</p>
                      )}
                    </div>
                  </div>
                  
                  {/* During Photos */}
                  <div>
                    <Label className="flex items-center gap-2 mb-2">
                      <Camera className="h-4 w-4" /> During Photos ({completionPack.during_photos?.length || 0})
                    </Label>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {(completionPack.during_photos || []).map((photo, idx) => (
                        <div key={idx} className="aspect-square bg-slate-100 rounded flex items-center justify-center">
                          <Image className="h-8 w-8 text-slate-400" />
                        </div>
                      ))}
                      {(completionPack.during_photos || []).length === 0 && (
                        <p className="text-sm text-slate-400 col-span-full">No during photos</p>
                      )}
                    </div>
                  </div>
                  
                  {/* After Photos */}
                  <div>
                    <Label className="flex items-center gap-2 mb-2">
                      <Camera className="h-4 w-4" /> After Photos ({completionPack.after_photos?.length || 0})
                    </Label>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {(completionPack.after_photos || []).map((photo, idx) => (
                        <div key={idx} className="aspect-square bg-slate-100 rounded flex items-center justify-center">
                          <Image className="h-8 w-8 text-slate-400" />
                        </div>
                      ))}
                      {(completionPack.after_photos || []).length === 0 && (
                        <p className="text-sm text-slate-400 col-span-full">No after photos</p>
                      )}
                    </div>
                  </div>
                  
                  {/* Compliance Documents */}
                  {(completionPack.compliance_documents || []).length > 0 && (
                    <div>
                      <Label className="flex items-center gap-2 mb-2">
                        <FileText className="h-4 w-4" /> Compliance Documents ({completionPack.compliance_documents.length})
                      </Label>
                      <div className="space-y-2">
                        {completionPack.compliance_documents.map((doc, idx) => (
                          <div key={idx} className="flex items-center gap-2 p-2 bg-slate-50 rounded">
                            <FileText className="h-4 w-4 text-slate-500" />
                            <span className="text-sm">{doc}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* General Attachments */}
              {safeArray(job.attachments).length === 0 && !completionPack ? (
                <div className="text-center py-8 text-slate-500">
                  <Paperclip className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No attachments</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {safeArray(job.attachments).map((att, idx) => (
                    <div key={att.id || idx} className="border rounded-lg p-3 text-center">
                      <FileText className="h-8 w-8 mx-auto mb-2 text-slate-400" />
                      <p className="text-xs truncate">{att.filename || att.original_filename || att.name}</p>
                    </div>
                  ))}
                </div>
              )}
              
              {job.requires_certificate && !completionPack?.compliance_documents?.length && (
                <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <span className="text-sm text-amber-700">This job requires a certificate to be uploaded before completion</span>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* ================================================================
            FINANCIALS TAB
        ================================================================ */}
        <TabsContent value="financials" className="mt-4">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-base">Financials</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">Estimated Cost</p>
                  <p className="text-xl font-bold">£{(job.benchmark?.avg || job.financials?.estimated_cost || 0).toFixed(2)}</p>
                </div>
                <div className="p-4 bg-green-50 rounded-lg">
                  <p className="text-sm text-slate-500">Approved PO Value</p>
                  <p className="text-xl font-bold text-green-700">
                    £{(job.approved_quote?.total_cost || job.approved_quote?.total || job.financials?.approved_cost || 0).toFixed(2)}
                  </p>
                </div>
                <div className="p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-slate-500">Invoice Amount</p>
                  <p className="text-xl font-bold text-blue-700">
                    £{(invoice?.amount || job.invoice_amount || job.financials?.actual_cost || 0).toFixed(2)}
                  </p>
                </div>
              </div>
              
              {/* PO Information */}
              {job.po_number && (
                <div className="mt-4 p-4 bg-[#1F7A78]/10 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Purchase Order</p>
                      <p className="text-lg font-bold text-[#1F7A78]">{job.po_number}</p>
                    </div>
                    <Badge className="bg-green-100 text-green-700">Issued</Badge>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Issued: {job.po_issued_at ? format(parseISO(job.po_issued_at), 'dd MMM yyyy') : '-'}
                  </p>
                </div>
              )}
              
              {/* Invoice Status */}
              {invoice && (
                <div className={`mt-4 p-4 rounded-lg ${invoice.has_issue ? 'bg-orange-50' : 'bg-green-50'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="text-sm text-slate-500">Invoice</p>
                      <p className="text-lg font-bold">{invoice.invoice_number || 'Pending'}</p>
                    </div>
                    <Badge className={invoice.has_issue ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'}>
                      {invoice.status || (invoice.has_issue ? 'Requires Review' : 'Approved')}
                    </Badge>
                  </div>
                  {invoice.has_issue && (
                    <div className="mt-2 p-2 bg-orange-100 rounded text-sm text-orange-700">
                      <strong>Invoice Requires Review:</strong>
                      <ul className="list-disc ml-4 mt-1">
                        {(invoice.issue_reasons || []).map((reason, idx) => (
                          <li key={idx}>{reason}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
              
              {/* Variance */}
              {invoice && (
                <div className="mt-4">
                  <div className="flex justify-between text-sm py-2 border-t">
                    <span>Variance (Invoice - PO)</span>
                    <span className={`font-medium ${(invoice.variance || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                      £{(invoice.variance || 0).toFixed(2)}
                    </span>
                  </div>
                </div>
              )}
              
              {/* Status Summary */}
              <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                <div>
                  <Label className="text-slate-500">Quote Status</Label>
                  <p className="font-medium">{job.approved_quote_id ? 'Approved' : (job.quotes?.length > 0 ? 'Pending' : 'None')}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Invoice Status</Label>
                  <p className="font-medium">{invoice?.status || job.invoice_status || 'None'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* ================================================================
            HISTORY TAB
        ================================================================ */}
        <TabsContent value="history" className="mt-4">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-base">History</CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                <div className="space-y-4">
                  {safeArray(job.history).length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <History className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p>No history recorded</p>
                    </div>
                  ) : (
                    [...safeArray(job.history)].reverse().map((entry, idx) => (
                      <div key={idx} className="flex gap-3">
                        <div className="w-2 h-2 rounded-full bg-[#1F7A78] mt-2 shrink-0"></div>
                        <div className="flex-1 pb-4 border-b border-dashed">
                          <p className="font-medium text-sm">{entry.action}</p>
                          <p className="text-sm text-slate-600">{entry.details}</p>
                          <p className="text-xs text-slate-400 mt-1">
                            {entry.timestamp && format(parseISO(entry.timestamp), 'dd MMM yyyy HH:mm')} - {entry.by}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      {/* ==================================================================
          DIALOGS
      ================================================================== */}
      
      {/* Assign Contractor Dialog - Enhanced with Marketplace Recommendations */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-teal-600" />
              Assign Contractor - {job.job_reference}
            </DialogTitle>
          </DialogHeader>
          
          {/* Marketplace Recommendations Panel */}
          <div className="mb-6">
            <JobRecommendationPanel 
              jobId={job.id || jobId} 
              onAssign={(contractor) => {
                toast.success(`Assigned to ${contractor.contractor_name}`);
                setShowAssignDialog(false);
                loadJob();
              }}
            />
          </div>
          
          {/* Divider */}
          <div className="relative my-4">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-2 text-gray-500">Or select manually</span>
            </div>
          </div>

          {/* Manual Selection Fallback */}
          <div className="space-y-4">
            <div>
              <Label>Contractor *</Label>
              <Select value={assignData.contractor_id} onValueChange={(v) => setAssignData({...assignData, contractor_id: v})}>
                <SelectTrigger data-testid="contractor-select">
                  <SelectValue placeholder="Select contractor" />
                </SelectTrigger>
                <SelectContent>
                  {contractors.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.company || c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Scheduled Date (Optional)</Label>
              <Input 
                type="date" 
                value={assignData.scheduled_date} 
                onChange={(e) => setAssignData({...assignData, scheduled_date: e.target.value})}
                data-testid="scheduled-date-input"
              />
            </div>
            <div>
              <Label>Scheduled Time (Optional)</Label>
              <Input 
                type="time" 
                value={assignData.scheduled_time} 
                onChange={(e) => setAssignData({...assignData, scheduled_time: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignDialog(false)}>Cancel</Button>
            <Button className="bg-[#1F7A78] hover:bg-[#16615f]" onClick={handleAssignContractor} data-testid="confirm-assign-btn">
              Assign Manually
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Add Note Dialog with Attachments */}
      <Dialog open={showNoteDialog} onOpenChange={(open) => {
        setShowNoteDialog(open);
        if (!open) {
          setNoteText('');
          setNoteAttachments([]);
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-[#1F7A78]" />
              Add Note / Communication
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Note Text */}
            <div>
              <Label>Note</Label>
              <Textarea 
                value={noteText} 
                onChange={(e) => setNoteText(e.target.value)}
                placeholder="Enter your note or instructions..."
                rows={4}
                data-testid="note-textarea"
              />
            </div>
            
            {/* File Attachments */}
            <div className="p-4 bg-slate-50 rounded-lg border">
              <Label className="flex items-center gap-2 mb-3">
                <Paperclip className="h-4 w-4 text-[#1F7A78]" />
                Attachments
                <span className="text-xs text-slate-500">({noteAttachments.length}/10 files)</span>
              </Label>
              
              <div className="flex gap-2 mb-3">
                <label className="cursor-pointer">
                  <input 
                    type="file" 
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={handleNoteFileSelect}
                    data-testid="note-image-input"
                  />
                  <Button type="button" variant="outline" size="sm" asChild>
                    <span><Camera className="h-4 w-4 mr-1" />Add Photos</span>
                  </Button>
                </label>
                <label className="cursor-pointer">
                  <input 
                    type="file" 
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.txt"
                    multiple
                    className="hidden"
                    onChange={handleNoteFileSelect}
                    data-testid="note-document-input"
                  />
                  <Button type="button" variant="outline" size="sm" asChild>
                    <span><FileText className="h-4 w-4 mr-1" />Add Documents</span>
                  </Button>
                </label>
              </div>
              
              {/* Attachment Previews */}
              {noteAttachments.length > 0 && (
                <div className="grid grid-cols-4 gap-2">
                  {noteAttachments.map(att => (
                    <div key={att.id} className="relative group">
                      {att.type === 'image' && att.preview ? (
                        <img 
                          src={att.preview} 
                          alt={att.name}
                          className="w-full h-16 object-cover rounded border"
                        />
                      ) : (
                        <div className="w-full h-16 bg-white rounded border flex items-center justify-center">
                          <FileText className="h-6 w-6 text-slate-400" />
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => removeNoteAttachment(att.id)}
                        className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="h-3 w-3" />
                      </button>
                      <p className="text-xs truncate mt-1" title={att.name}>{att.name}</p>
                    </div>
                  ))}
                </div>
              )}
              
              {noteAttachments.length === 0 && (
                <p className="text-xs text-slate-400 text-center py-2">No attachments added</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNoteDialog(false)}>Cancel</Button>
            <Button 
              className="bg-[#1F7A78] hover:bg-[#16615f]" 
              onClick={handleAddNote} 
              disabled={noteUploading}
              data-testid="confirm-note-btn"
            >
              {noteUploading ? (
                <>
                  <span className="animate-spin mr-2">⏳</span>
                  Uploading...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Note
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Completion Pack Dialog */}
      <Dialog open={showCompletionDialog} onOpenChange={setShowCompletionDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileCheck className="h-5 w-5" />
              Submit Completion Pack
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Photo Uploads */}
            <div className="grid grid-cols-3 gap-4">
              {/* Before Photos */}
              <div>
                <Label className="flex items-center gap-1 mb-2">
                  <Camera className="h-3 w-3" /> Before Photos
                </Label>
                <div className="border-2 border-dashed rounded-lg p-3 text-center">
                  <input 
                    type="file" 
                    multiple 
                    accept="image/*"
                    className="hidden" 
                    id="before-photos"
                    onChange={(e) => handleFileUpload('before_photos', e.target.files)}
                  />
                  <label htmlFor="before-photos" className="cursor-pointer">
                    <Upload className="h-6 w-6 mx-auto text-slate-400" />
                    <p className="text-xs text-slate-500 mt-1">Click to upload</p>
                  </label>
                </div>
                <div className="mt-2 space-y-1">
                  {completionData.before_photos.map((f, i) => (
                    <div key={i} className="flex items-center justify-between text-xs bg-slate-50 p-1 rounded">
                      <span className="truncate">{f}</span>
                      <button onClick={() => handleRemoveFile('before_photos', i)} className="text-red-500">
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* During Photos */}
              <div>
                <Label className="flex items-center gap-1 mb-2">
                  <Camera className="h-3 w-3" /> During Photos
                </Label>
                <div className="border-2 border-dashed rounded-lg p-3 text-center">
                  <input 
                    type="file" 
                    multiple 
                    accept="image/*"
                    className="hidden" 
                    id="during-photos"
                    onChange={(e) => handleFileUpload('during_photos', e.target.files)}
                  />
                  <label htmlFor="during-photos" className="cursor-pointer">
                    <Upload className="h-6 w-6 mx-auto text-slate-400" />
                    <p className="text-xs text-slate-500 mt-1">Click to upload</p>
                  </label>
                </div>
                <div className="mt-2 space-y-1">
                  {completionData.during_photos.map((f, i) => (
                    <div key={i} className="flex items-center justify-between text-xs bg-slate-50 p-1 rounded">
                      <span className="truncate">{f}</span>
                      <button onClick={() => handleRemoveFile('during_photos', i)} className="text-red-500">
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* After Photos */}
              <div>
                <Label className="flex items-center gap-1 mb-2">
                  <Camera className="h-3 w-3" /> After Photos *
                </Label>
                <div className="border-2 border-dashed rounded-lg p-3 text-center border-amber-300">
                  <input 
                    type="file" 
                    multiple 
                    accept="image/*"
                    className="hidden" 
                    id="after-photos"
                    onChange={(e) => handleFileUpload('after_photos', e.target.files)}
                  />
                  <label htmlFor="after-photos" className="cursor-pointer">
                    <Upload className="h-6 w-6 mx-auto text-amber-500" />
                    <p className="text-xs text-amber-600 mt-1">Required</p>
                  </label>
                </div>
                <div className="mt-2 space-y-1">
                  {completionData.after_photos.map((f, i) => (
                    <div key={i} className="flex items-center justify-between text-xs bg-slate-50 p-1 rounded">
                      <span className="truncate">{f}</span>
                      <button onClick={() => handleRemoveFile('after_photos', i)} className="text-red-500">
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Compliance Documents (if required) */}
            {job.requires_certificate && (
              <div>
                <Label className="flex items-center gap-1 mb-2 text-amber-600">
                  <FileText className="h-3 w-3" /> Compliance Documents (Required)
                </Label>
                <div className="border-2 border-dashed border-amber-300 rounded-lg p-3 text-center">
                  <input 
                    type="file" 
                    multiple 
                    accept=".pdf,.doc,.docx"
                    className="hidden" 
                    id="compliance-docs"
                    onChange={(e) => handleFileUpload('compliance_documents', e.target.files)}
                  />
                  <label htmlFor="compliance-docs" className="cursor-pointer">
                    <Upload className="h-6 w-6 mx-auto text-amber-500" />
                    <p className="text-xs text-amber-600 mt-1">Upload certificates/compliance docs</p>
                  </label>
                </div>
                <div className="mt-2 space-y-1">
                  {completionData.compliance_documents.map((f, i) => (
                    <div key={i} className="flex items-center justify-between text-xs bg-amber-50 p-1 rounded">
                      <span className="truncate">{f}</span>
                      <button onClick={() => handleRemoveFile('compliance_documents', i)} className="text-red-500">
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Text Fields */}
            <div>
              <Label>Works Summary *</Label>
              <Textarea 
                value={completionData.works_summary}
                onChange={(e) => setCompletionData({...completionData, works_summary: e.target.value})}
                placeholder="Describe the work completed..."
                rows={3}
                data-testid="works-summary-input"
              />
            </div>
            
            <div>
              <Label>Completion Notes *</Label>
              <Textarea 
                value={completionData.completion_notes}
                onChange={(e) => setCompletionData({...completionData, completion_notes: e.target.value})}
                placeholder="Any additional notes or observations..."
                rows={3}
                data-testid="completion-notes-input"
              />
            </div>
            
            <div>
              <Label>Materials Used</Label>
              <Textarea 
                value={completionData.materials_used}
                onChange={(e) => setCompletionData({...completionData, materials_used: e.target.value})}
                placeholder="List materials used..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCompletionDialog(false)}>Cancel</Button>
            <Button 
              className="bg-green-600 hover:bg-green-700" 
              onClick={handleSubmitCompletion}
              data-testid="submit-completion-btn"
            >
              <CheckCircle className="h-4 w-4 mr-1" />
              Submit Completion Pack
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Review Dialog */}
      <Dialog open={showReviewDialog} onOpenChange={setShowReviewDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardCheck className="h-5 w-5" />
              Internal Review Gate
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Review Outcome */}
            <div>
              <Label>Review Outcome *</Label>
              <div className="flex gap-2 mt-2">
                <Button 
                  variant={reviewData.outcome === 'Approve' ? 'default' : 'outline'}
                  className={reviewData.outcome === 'Approve' ? 'bg-green-600 hover:bg-green-700' : ''}
                  onClick={() => setReviewData({...reviewData, outcome: 'Approve'})}
                  data-testid="review-approve-btn"
                >
                  <CheckCircle className="h-4 w-4 mr-1" />
                  Approve
                </Button>
                <Button 
                  variant={reviewData.outcome === 'Reject' ? 'default' : 'outline'}
                  className={reviewData.outcome === 'Reject' ? 'bg-red-600 hover:bg-red-700' : ''}
                  onClick={() => setReviewData({...reviewData, outcome: 'Reject'})}
                  data-testid="review-reject-btn"
                >
                  <XCircle className="h-4 w-4 mr-1" />
                  Reject
                </Button>
                <Button 
                  variant={reviewData.outcome === 'Query' ? 'default' : 'outline'}
                  className={reviewData.outcome === 'Query' ? 'bg-orange-600 hover:bg-orange-700' : ''}
                  onClick={() => setReviewData({...reviewData, outcome: 'Query'})}
                  data-testid="review-query-btn"
                >
                  <HelpCircle className="h-4 w-4 mr-1" />
                  Query
                </Button>
              </div>
            </div>
            
            {/* Review Checklist */}
            <div className="space-y-3 p-3 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-2">
                <Checkbox 
                  checked={reviewData.scope_aligned}
                  onCheckedChange={(v) => setReviewData({...reviewData, scope_aligned: v})}
                />
                <Label className="text-sm">Scope Aligned</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox 
                  checked={reviewData.satisfactory}
                  onCheckedChange={(v) => setReviewData({...reviewData, satisfactory: v})}
                />
                <Label className="text-sm">Satisfactory Standard</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox 
                  checked={reviewData.evidence_complete}
                  onCheckedChange={(v) => setReviewData({...reviewData, evidence_complete: v})}
                />
                <Label className="text-sm">Evidence Complete</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox 
                  checked={reviewData.compliance_complete}
                  onCheckedChange={(v) => setReviewData({...reviewData, compliance_complete: v})}
                />
                <Label className="text-sm">Compliance Complete</Label>
              </div>
            </div>
            
            {/* Comments */}
            <div>
              <Label>Review Comments {reviewData.outcome !== 'Approve' && '*'}</Label>
              <Textarea 
                value={reviewData.comments}
                onChange={(e) => setReviewData({...reviewData, comments: e.target.value})}
                placeholder={reviewData.outcome === 'Approve' ? 'Optional comments...' : 'Required for reject/query...'}
                rows={3}
                data-testid="review-comments-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReviewDialog(false)}>Cancel</Button>
            <Button 
              className={
                reviewData.outcome === 'Approve' ? 'bg-green-600 hover:bg-green-700' :
                reviewData.outcome === 'Reject' ? 'bg-red-600 hover:bg-red-700' :
                'bg-orange-600 hover:bg-orange-700'
              }
              onClick={handleSubmitReview}
              data-testid="submit-review-btn"
            >
              Submit Review
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Invoice Dialog */}
      <Dialog open={showInvoiceDialog} onOpenChange={setShowInvoiceDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5" />
              Submit Invoice
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* PO Reference */}
            {job.po_number && (
              <div className="p-3 bg-[#1F7A78]/10 rounded-lg">
                <p className="text-sm text-slate-500">PO Reference</p>
                <p className="font-bold text-[#1F7A78]">{job.po_number}</p>
                <p className="text-sm">Approved Value: £{(job.approved_quote?.total_cost || job.approved_quote?.total || 0).toFixed(2)}</p>
              </div>
            )}
            
            <div>
              <Label>Invoice Number</Label>
              <Input 
                value={invoiceData.invoice_number}
                onChange={(e) => setInvoiceData({...invoiceData, invoice_number: e.target.value})}
                placeholder="INV-001"
                data-testid="invoice-number-input"
              />
            </div>
            
            <div>
              <Label>Invoice Date</Label>
              <Input 
                type="date"
                value={invoiceData.invoice_date}
                onChange={(e) => setInvoiceData({...invoiceData, invoice_date: e.target.value})}
              />
            </div>
            
            <div>
              <Label>Invoice Amount (£) *</Label>
              <Input 
                type="number"
                step="0.01"
                value={invoiceData.amount}
                onChange={(e) => setInvoiceData({...invoiceData, amount: e.target.value})}
                placeholder="0.00"
                data-testid="invoice-amount-input"
              />
              {job.approved_quote && parseFloat(invoiceData.amount) > (job.approved_quote.total_cost || job.approved_quote.total || 0) && (
                <p className="text-xs text-orange-600 mt-1 flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  Invoice exceeds PO value - will require review
                </p>
              )}
            </div>
            
            <div>
              <Label>Notes</Label>
              <Textarea 
                value={invoiceData.notes}
                onChange={(e) => setInvoiceData({...invoiceData, notes: e.target.value})}
                placeholder="Any additional notes..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInvoiceDialog(false)}>Cancel</Button>
            <Button 
              className="bg-blue-600 hover:bg-blue-700"
              onClick={handleSubmitInvoice}
              data-testid="submit-invoice-btn"
            >
              <Receipt className="h-4 w-4 mr-1" />
              Submit Invoice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Quote Dialog (simplified) */}
      <Dialog open={showQuoteDialog} onOpenChange={setShowQuoteDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add Quote</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-500">NO QUOTE BREAKDOWN = REJECT</p>
            
            {/* Line Items */}
            <div className="space-y-2">
              <Label>Line Items</Label>
              {quoteData.line_items.map((item, idx) => (
                <div key={idx} className="flex gap-2 items-end">
                  <div className="flex-1">
                    <Input 
                      placeholder="Description"
                      value={item.description}
                      onChange={(e) => handleUpdateQuoteLine(idx, 'description', e.target.value)}
                    />
                  </div>
                  <div className="w-20">
                    <Input 
                      type="number"
                      placeholder="Qty"
                      value={item.quantity}
                      onChange={(e) => handleUpdateQuoteLine(idx, 'quantity', parseInt(e.target.value) || 1)}
                    />
                  </div>
                  <div className="w-24">
                    <Input 
                      type="number"
                      step="0.01"
                      placeholder="Unit £"
                      value={item.unit_cost}
                      onChange={(e) => handleUpdateQuoteLine(idx, 'unit_cost', parseFloat(e.target.value) || 0)}
                    />
                  </div>
                  <div className="w-20 text-right font-medium">
                    £{(item.quantity * item.unit_cost).toFixed(2)}
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => handleRemoveQuoteLine(idx)}>
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={handleAddQuoteLine}>
                <Plus className="h-4 w-4 mr-1" />
                Add Line
              </Button>
            </div>
            
            <div className="border-t pt-3">
              <div className="flex justify-between font-medium">
                <span>Total</span>
                <span>£{calculateQuoteTotal().toFixed(2)}</span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowQuoteDialog(false)}>Cancel</Button>
            <Button className="bg-[#1F7A78] hover:bg-[#16615f]">
              Submit Quote
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Share Task Dialog */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="h-5 w-5 text-[#1F7A78]" />
              Share Task
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Share job sheet <span className="font-semibold">{job?.job_reference}</span> with contractors or clients.
            </p>
            
            {/* Download PDF */}
            <div className="p-3 bg-slate-50 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-sm">Download PDF</p>
                  <p className="text-xs text-slate-500">Get a professional job sheet PDF</p>
                </div>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={handleDownloadPDF}
                  data-testid="download-pdf-btn"
                >
                  <Download className="h-4 w-4 mr-1" />
                  Download
                </Button>
              </div>
            </div>
            
            {/* Email PDF */}
            <div className="p-3 bg-slate-50 rounded-lg space-y-2">
              <div>
                <p className="font-medium text-sm">Email Job Sheet</p>
                <p className="text-xs text-slate-500">Send job details via email</p>
              </div>
              <div className="flex gap-2">
                <Input 
                  placeholder="recipient@email.com"
                  type="email"
                  value={shareEmail}
                  onChange={(e) => setShareEmail(e.target.value)}
                  data-testid="share-email-input"
                />
                <Button 
                  size="sm" 
                  className="bg-[#1F7A78] hover:bg-[#16615f]"
                  onClick={handleEmailPDF}
                  disabled={shareLoading}
                  data-testid="send-email-btn"
                >
                  {shareLoading ? (
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                  ) : (
                    <>
                      <Mail className="h-4 w-4 mr-1" />
                      Send
                    </>
                  )}
                </Button>
              </div>
            </div>
            
            {/* WhatsApp Share */}
            <div className="p-3 bg-slate-50 rounded-lg space-y-2">
              <div>
                <p className="font-medium text-sm">WhatsApp</p>
                <p className="text-xs text-slate-500">Share job details via WhatsApp</p>
              </div>
              <div className="flex gap-2">
                <Input 
                  placeholder="Phone (optional)"
                  value={sharePhone}
                  onChange={(e) => setSharePhone(e.target.value)}
                  data-testid="share-phone-input"
                />
                <Button 
                  size="sm" 
                  className="bg-green-600 hover:bg-green-700"
                  onClick={handleWhatsAppShare}
                  data-testid="whatsapp-share-btn"
                >
                  <Phone className="h-4 w-4 mr-1" />
                  Share
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowShareDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Completion Check Dialog (Enforced) */}
      <Dialog open={showCompletionCheckDialog} onOpenChange={setShowCompletionCheckDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardCheck className="h-5 w-5 text-[#1F7A78]" />
              Complete Job - Requirements Check
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {completionCheck && (
              <>
                {/* Completion Status */}
                <div className={`p-4 rounded-lg ${completionCheck.valid ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                  <p className={`font-semibold ${completionCheck.valid ? 'text-green-700' : 'text-red-700'}`}>
                    {completionCheck.valid ? '✓ All requirements met - Ready to complete' : '✗ Missing requirements'}
                  </p>
                </div>
                
                {/* Errors */}
                {completionCheck.errors?.length > 0 && (
                  <div className="space-y-2">
                    <p className="font-medium text-red-700">Required before completion:</p>
                    <ul className="list-disc list-inside space-y-1 text-sm text-red-600">
                      {completionCheck.errors.map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {/* Warnings */}
                {completionCheck.warnings?.length > 0 && (
                  <div className="space-y-2">
                    <p className="font-medium text-amber-700">Warnings (optional):</p>
                    <ul className="list-disc list-inside space-y-1 text-sm text-amber-600">
                      {completionCheck.warnings.map((warn, i) => (
                        <li key={i}>{warn}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {/* Upload Section */}
                <div className="border-t pt-4 space-y-3">
                  <p className="font-medium text-slate-700">Upload Required Documents:</p>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Job Sheet</Label>
                      <Input 
                        type="file"
                        onChange={(e) => handleUploadCompletionDoc('job_sheet', e.target.files[0])}
                        disabled={uploadingDoc}
                        className="text-xs"
                        data-testid="upload-job-sheet"
                      />
                      {job?.job_sheet_uploaded && <span className="text-xs text-green-600">✓ Uploaded</span>}
                    </div>
                    <div>
                      <Label className="text-xs">Before Photos</Label>
                      <Input 
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleUploadCompletionDoc('before_photos', e.target.files[0])}
                        disabled={uploadingDoc}
                        className="text-xs"
                        data-testid="upload-before-photos"
                      />
                      {job?.before_photos_uploaded && <span className="text-xs text-green-600">✓ Uploaded</span>}
                    </div>
                    <div>
                      <Label className="text-xs">During Photos</Label>
                      <Input 
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleUploadCompletionDoc('during_photos', e.target.files[0])}
                        disabled={uploadingDoc}
                        className="text-xs"
                        data-testid="upload-during-photos"
                      />
                      {job?.during_photos_uploaded && <span className="text-xs text-green-600">✓ Uploaded</span>}
                    </div>
                    <div>
                      <Label className="text-xs">After Photos</Label>
                      <Input 
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleUploadCompletionDoc('after_photos', e.target.files[0])}
                        disabled={uploadingDoc}
                        className="text-xs"
                        data-testid="upload-after-photos"
                      />
                      {job?.after_photos_uploaded && <span className="text-xs text-green-600">✓ Uploaded</span>}
                    </div>
                  </div>
                </div>
                
                {/* Completion Uploads Status */}
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-sm font-medium mb-2">Current Uploads:</p>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className={completionCheck.completion_uploads?.job_sheet ? 'text-green-600' : 'text-red-600'}>
                      Job Sheet: {completionCheck.completion_uploads?.job_sheet ? '✓' : '✗'}
                    </div>
                    <div className={completionCheck.completion_uploads?.before_photos ? 'text-green-600' : 'text-red-600'}>
                      Before Photos: {completionCheck.completion_uploads?.before_photos ? '✓' : '✗'}
                    </div>
                    <div className={completionCheck.completion_uploads?.during_photos ? 'text-green-600' : 'text-amber-600'}>
                      During Photos: {completionCheck.completion_uploads?.during_photos ? '✓' : '○'}
                    </div>
                    <div className={completionCheck.completion_uploads?.after_photos ? 'text-green-600' : 'text-red-600'}>
                      After Photos: {completionCheck.completion_uploads?.after_photos ? '✓' : '✗'}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCompletionCheckDialog(false)}>Cancel</Button>
            <Button 
              className="bg-green-600 hover:bg-green-700"
              onClick={handleCompleteEnforced}
              disabled={!completionCheck?.valid}
              data-testid="confirm-complete-btn"
            >
              <CheckCircle className="h-4 w-4 mr-1" />
              Complete Job
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Upload Documents Dialog */}
      <Dialog open={showUploadDocsDialog} onOpenChange={setShowUploadDocsDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5 text-[#1F7A78]" />
              Upload Documents
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Upload required completion documents and certificates for {job?.job_reference}.
            </p>
            
            <div className="space-y-3">
              <div>
                <Label>Job Sheet</Label>
                <Input 
                  type="file"
                  onChange={(e) => handleUploadCompletionDoc('job_sheet', e.target.files[0])}
                  disabled={uploadingDoc}
                  data-testid="dialog-upload-job-sheet"
                />
              </div>
              <div>
                <Label>Before Photos</Label>
                <Input 
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleUploadCompletionDoc('before_photos', e.target.files[0])}
                  disabled={uploadingDoc}
                  data-testid="dialog-upload-before"
                />
              </div>
              <div>
                <Label>During Photos</Label>
                <Input 
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleUploadCompletionDoc('during_photos', e.target.files[0])}
                  disabled={uploadingDoc}
                  data-testid="dialog-upload-during"
                />
              </div>
              <div>
                <Label>After Photos</Label>
                <Input 
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleUploadCompletionDoc('after_photos', e.target.files[0])}
                  disabled={uploadingDoc}
                  data-testid="dialog-upload-after"
                />
              </div>
              
              <div className="border-t pt-3">
                <Label>Certificate (if required)</Label>
                <Select onValueChange={(val) => document.getElementById('cert-type-hidden').value = val}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select certificate type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="EICR">EICR</SelectItem>
                    <SelectItem value="Gas Safety Certificate">Gas Safety Certificate</SelectItem>
                    <SelectItem value="Boiler Service Certificate">Boiler Service Certificate</SelectItem>
                    <SelectItem value="Fire / Alarm Certificate">Fire / Alarm Certificate</SelectItem>
                    <SelectItem value="Legionella Report">Legionella Report</SelectItem>
                  </SelectContent>
                </Select>
                <input type="hidden" id="cert-type-hidden" />
                <Input 
                  type="file"
                  className="mt-2"
                  onChange={(e) => {
                    const certType = document.getElementById('cert-type-hidden').value;
                    if (certType && e.target.files[0]) {
                      handleUploadDocument(certType, e.target.files[0]);
                    } else {
                      toast.error('Please select certificate type first');
                    }
                  }}
                  disabled={uploadingDoc}
                  data-testid="dialog-upload-cert"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadDocsDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Create Invoice from Job Dialog */}
      <Dialog open={showCreateInvoiceDialog} onOpenChange={setShowCreateInvoiceDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5 text-blue-600" />
              Create Invoice from Job
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-700">
                Create an invoice based on the approved quote for this job.
              </p>
            </div>
            
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500">Job Reference:</span>
                <span className="font-medium">{job?.job_reference}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">PO Number:</span>
                <span className="font-medium text-[#1F7A78]">{job?.po_number}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Approved Quote Value:</span>
                <span className="font-medium">
                  £{(job?.approved_quote?.total_cost || job?.approved_quote?.total || 0).toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Contractor:</span>
                <span className="font-medium">{job?.assigned_contractor_name || '-'}</span>
              </div>
            </div>
            
            <div className="border-t pt-3">
              <p className="text-xs text-slate-500">
                Invoice will be created with values from the approved quote. 
                NO PO = NO PAYMENT policy applies.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateInvoiceDialog(false)}>Cancel</Button>
            <Button 
              className="bg-blue-600 hover:bg-blue-700"
              onClick={handleCreateInvoiceFromJob}
              disabled={creatingInvoice}
              data-testid="confirm-create-invoice-btn"
            >
              {creatingInvoice ? (
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-1" />
              ) : (
                <Receipt className="h-4 w-4 mr-1" />
              )}
              Create Invoice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Approve Quote Dialog */}
      <Dialog open={showApproveQuoteDialog} onOpenChange={setShowApproveQuoteDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileCheck className="h-5 w-5 text-purple-600" />
              Approve Quote
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Review and approve a quote for {job?.job_reference}. This will generate a PO number.
            </p>
            
            {job?.quotes?.filter(q => q.status === 'Pending').map((quote) => (
              <div key={quote.id} className="border rounded-lg p-4 space-y-3">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium">{quote.contractor_name || 'Contractor'}</p>
                    <p className="text-xs text-slate-500">Quote ID: {quote.id}</p>
                  </div>
                  <Badge className={quote.evaluation_result === 'ACCEPT' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                    {quote.evaluation_result || 'Pending Review'}
                  </Badge>
                </div>
                
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-slate-500">Labour:</span>
                    <span className="ml-2">£{(quote.labour_cost || 0).toFixed(2)}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Materials:</span>
                    <span className="ml-2">£{(quote.materials_cost || 0).toFixed(2)}</span>
                  </div>
                </div>
                
                <div className="flex justify-between items-center border-t pt-2">
                  <div>
                    <span className="text-slate-500">Total:</span>
                    <span className="ml-2 font-bold text-lg">£{(quote.total_cost || 0).toFixed(2)}</span>
                  </div>
                  <Button 
                    size="sm"
                    className="bg-purple-600 hover:bg-purple-700"
                    onClick={() => handleApproveQuote(quote.id)}
                    disabled={approvingQuote}
                    data-testid={`approve-quote-${quote.id}`}
                  >
                    {approvingQuote ? (
                      <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                    ) : (
                      <>
                        <CheckCircle className="h-4 w-4 mr-1" />
                        Approve & Issue PO
                      </>
                    )}
                  </Button>
                </div>
              </div>
            ))}
            
            {safeArray(job?.quotes).filter(q => q.status === 'Pending').length === 0 && (
              <div className="text-center py-4 text-slate-500">
                No pending quotes to approve
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApproveQuoteDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
